// 本文（生 Markdown）の「raw offset ↔ 編集時の可視区間 ↔ 表示時の縮約区間」の対応表を求める
// 共有モジュール。
//
// 方針:
// - 行単位のブロック記法（見出し・箇条書き・引用・水平線・番号付きリスト）の判定と、
//   表示テキスト付きリンク（`[label](URL)`）・素 URL の分割判定を、web（編集 DOM / 非編集表示 / PDF）
//   と domain / API 検証が同じ実装で参照するための正本とする。以前は apps/web の markdown-text.ts と linkify-text.ts が
//   それぞれ持っていた判定を、結果を変えずにここへ集約した（web 側は本モジュールを再輸出する）。
// - 「raw offset」は保存本文（object.text）先頭からの UTF-16 code unit 数で、JavaScript の
//   文字列・Selection API と同じ座標系。部分書式（text-inline-style.ts）の run もこの座標系を使う。
// - 本モジュールは純粋関数のみで、DOM・CSS・React へ依存しない。

// 保存済み列幅の形（永続フィールドの型）だけを型として借りる。値の import は無いため、
// オブジェクト型定義（canvas-object.ts）との循環は生じない。
import type { TableColumnWidths } from "./canvas-object";

export type MarkdownLineKind =
  | "heading"
  | "bullet"
  | "task"
  | "quote"
  | "ordered"
  | "rule"
  | "plain"
  | "blank";

/** 見出しレベル（`#` の個数）。 */
export type MarkdownHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * 1 行を分類したうえで「生の記法プレフィックス（本文の手前）」と「本文」を分離した結果。
 * WYSIWYG 編集（markdown-wysiwyg.ts）が、記法マーカーを表示 DOM のテキストから隠しつつ
 * `rawPrefix + content` で生 Markdown を完全復元するために使う。パーサの単一の真実（本モジュール）
 * を共有し、非編集表示（parseMarkdownTextLines）と分類規則が一致することを保証する。
 *
 * 不変条件: 任意の行 `line` について `rawPrefix + content === line`（blank は両方空）。
 */
export interface MarkdownLineParts {
  readonly kind: MarkdownLineKind;
  /** heading: 1〜6 / bullet・ordered: 0〜BULLET_MAX_LEVEL / quote・rule・plain・blank: 0。 */
  readonly level: number;
  /**
   * マーカーを除いた本文（blank は空文字列）。
   * rule だけは rawPrefix を空にして行全体（`---`）を本文に残す。編集中もマーカーを文字として
   * 表示し続けるためで、隠すと「見えない文字だけの行」になって削除・編集ができなくなる。
   */
  readonly content: string;
  /**
   * 記法マーカー部分（見出し `## `・箇条書き `  - `・引用 `> `・番号付き `  1. ` など）。
   * rule・plain・blank は空文字列。
   */
  readonly rawPrefix: string;
  /**
   * 箇条書きのネスト別グリフ（•/◦/•）。bullet 以外は空文字列。
   * 番号付きリストの項番は他行の採番に依存するため、ここではなく
   * `MarkdownSourceMap.orderedNumbers`（assignOrderedListNumbers）が決める。
   */
  readonly glyph: string;
  /** task 行の完了状態。task 以外は常に false。 */
  readonly checked: boolean;
}

/**
 * 箇条書きのネストレベル上限（これを超える深さはクランプする）。
 * デザインシステム「箇条書きリスト」の「ネストは最大 3 階層」に合わせ、level は 0〜2 の 3 値とする。
 * 行頭スペース 6 個以上（4 階層目以降）の字下げは表示上 level 2 へ丸めるが、保存本文は変更しない。
 * 編集中の Tab / Backspace によるネスト操作の上限（use-object-text-editor.ts）にも同じ値を使う。
 */
export const BULLET_MAX_LEVEL = 2;

/**
 * ネストレベル別の箇条書きマーカー（上限以降は最後のマーカーを使う）。
 * デザインシステム「箇条書きリスト」の list-style-type（disc → circle → disc）に合わせる。
 */
export const BULLET_MARKERS: readonly string[] = ["•", "◦", "•"];

// 見出し: 行頭の # 1〜6 個 + 半角スペース + 本文（空白のみの本文は見出しにしない）。
// 行頭に字下げがある # は見出しとして扱わない（箇条書きネストの解釈と衝突するため）。
const HEADING_PATTERN = /^(#{1,6}) (.*)$/;

// 箇条書き: 行頭の半角スペース（ネスト）+ マーカー（- / * / +）+ 半角スペース + 本文。
// タブ字下げは ( *) にマッチしないため自然に plain 扱いになる。
const BULLET_PATTERN = /^( *)([-*+]) (.*)$/;

// タスクリスト: 箇条書きマーカー + 半角スペース + `[ ]` / `[x]` / `[X]`。
// 本文前の半角スペースは任意で、無い場合も空のタスクとして認識する。
const TASK_PATTERN = /^( *)([-*+]) \[([ xX])\](?: (.*))?$/;

// 引用: 行頭の > + 半角スペース + 本文、または > だけの行。
// `>引用`（空白なし）は記法として扱わない（半角スペースを必須にする。箇条書き・見出しと同じ規則）。
// 字下げ付きの > は箇条書きのネスト解釈と衝突するため記法として扱わない。
// 入れ子（>>）は「> の直後が半角スペースでない」ため記法にならず plain になる。
// 入れ子として書きたい場合は `> > 二重` のように空白で区切る（本文は再解釈しない）。
const QUOTE_PATTERN = /^>(?: (.*))?$/;

// 水平線: 行頭から末尾までがハイフン 3 個以上（末尾の半角スペースは無視する）。
// *** は太字・斜体のマーカーと衝突し、___ はアンダースコア記法を採らない方針のため対象外。
// 行頭の空白は許さない（字下げは箇条書きのネスト解釈と衝突するため plain に落とす）。
const RULE_PATTERN = /^-{3,} *$/;

/**
 * 番号付きリストの数字として受け付ける最大桁数。
 * これを超える桁数（`1234567890. x`）は記法として扱わず plain のまま表示する。
 */
export const ORDERED_NUMBER_MAX_DIGITS = 9;

/**
 * 番号付きリストの数字の上限（9 桁の最大値）。
 * 編集中の Enter による継続では、この値の行の次を「+1」せずに同じ数字で挿入する
 * （10 桁にすると記法として認識されず、番号付きリストの継続という契約が壊れるため）。
 */
export const ORDERED_NUMBER_MAX = 10 ** ORDERED_NUMBER_MAX_DIGITS - 1;

// 番号付きリスト: 行頭スペース（ネスト）+ 1〜9 桁の数字 + ピリオド + 半角スペース + 本文。
// `1)` 形式は対象外。桁数の上限は ORDERED_NUMBER_MAX_DIGITS と必ず一致させる。
const ORDERED_PATTERN = new RegExp(`^( *)(\\d{1,${ORDERED_NUMBER_MAX_DIGITS}})\\. (.*)$`);

/**
 * 1 行を分類し、記法プレフィックスと本文を分離して返す。
 * 改行は含まない 1 行を渡すこと（呼び出し側で `/\r?\n/` 分割してから渡す）。
 *
 * 判定順は「見出し → 引用 → 水平線 → 番号付き → 箇条書き」。先頭 1 文字（`#` / `>` / `-` / 数字）で
 * ほぼ排他になるが、`---` と箇条書きの `- ` は前者を先に判定する必要がある
 * （BULLET_PATTERN はマーカー直後の半角スペースを要求するため現状は一致しないが、
 * 将来のパターン変更で取り違えないよう順序で保証する）。
 */
export function parseMarkdownLineParts(line: string): MarkdownLineParts {
  if (line.length === 0) {
    return { checked: false, content: "", glyph: "", kind: "blank", level: 0, rawPrefix: "" };
  }
  const heading = HEADING_PATTERN.exec(line);
  if (heading && heading[1] !== undefined && heading[2] !== undefined) {
    const content = heading[2];
    const level = markdownHeadingLevel(heading[1].length);
    // `# `（本文が空白のみ）は見出しにせず plain のまま扱う。
    if (level !== undefined && content.trim().length > 0) {
      return {
        checked: false,
        content,
        glyph: "",
        kind: "heading",
        level,
        rawPrefix: `${heading[1]} `,
      };
    }
    return { checked: false, content: line, glyph: "", kind: "plain", level: 0, rawPrefix: "" };
  }
  const quote = QUOTE_PATTERN.exec(line);
  if (quote) {
    // `> 本文` は rawPrefix "> "、マーカーだけの `>` は rawPrefix ">"（本文は空）。
    const content = quote[1];
    if (content === undefined) {
      return { checked: false, content: "", glyph: "", kind: "quote", level: 0, rawPrefix: ">" };
    }
    return { checked: false, content, glyph: "", kind: "quote", level: 0, rawPrefix: "> " };
  }
  if (RULE_PATTERN.test(line)) {
    // 水平線は rawPrefix を空にして行全体を本文に残す（編集中も `---` を文字として表示する）。
    return { checked: false, content: line, glyph: "", kind: "rule", level: 0, rawPrefix: "" };
  }
  const ordered = ORDERED_PATTERN.exec(line);
  if (ordered && ordered[1] !== undefined && ordered[2] !== undefined && ordered[3] !== undefined) {
    const level = Math.min(Math.floor(ordered[1].length / 2), BULLET_MAX_LEVEL);
    return {
      checked: false,
      content: ordered[3],
      // 項番は採番の後段（assignOrderedListNumbers）が決めるため、行単体では空にする。
      glyph: "",
      kind: "ordered",
      level,
      // 先頭スペース（ネスト）+ 数字 + ピリオド + 半角スペースを、入力どおりそのまま保持する。
      rawPrefix: `${ordered[1]}${ordered[2]}. `,
    };
  }
  const task = TASK_PATTERN.exec(line);
  if (task && task[1] !== undefined && task[2] !== undefined && task[3] !== undefined) {
    const content = task[4] ?? "";
    const level = Math.min(Math.floor(task[1].length / 2), BULLET_MAX_LEVEL);
    return {
      checked: task[3] === "x" || task[3] === "X",
      content,
      glyph: "",
      kind: "task",
      level,
      rawPrefix: line.slice(0, line.length - content.length),
    };
  }
  const bullet = BULLET_PATTERN.exec(line);
  if (bullet && bullet[1] !== undefined && bullet[2] !== undefined && bullet[3] !== undefined) {
    const level = Math.min(Math.floor(bullet[1].length / 2), BULLET_MAX_LEVEL);
    return {
      checked: false,
      content: bullet[3],
      glyph: bulletMarkerFor(level),
      kind: "bullet",
      level,
      // 先頭スペース（ネスト）+ マーカー + 半角スペースを、入力どおりそのまま保持する。
      rawPrefix: `${bullet[1]}${bullet[2]} `,
    };
  }
  return { checked: false, content: line, glyph: "", kind: "plain", level: 0, rawPrefix: "" };
}

/** ネストレベルに応じた箇条書きマーカーを返す。 */
export function bulletMarkerFor(level: number): string {
  const index = Math.min(Math.max(0, level), BULLET_MARKERS.length - 1);
  return BULLET_MARKERS[index] ?? "•";
}

/**
 * 番号付きリスト行の生プレフィックス（`  007. ` など）に書かれている数字を、文字列のまま返す。
 * ordered 以外の行、または数字を取り出せない場合は undefined。
 * 先頭ゼロを含む利用者の入力そのものが必要な処理（編集中の Tab によるネスト変更で
 * 「行に書かれている数字をそのまま保つ」）が使う。数値が必要なら markdownOrderedSourceNumber を使う。
 */
export function markdownOrderedSourceDigits(parts: MarkdownLineParts): string | undefined {
  if (parts.kind !== "ordered") return undefined;
  return /(\d+)\. $/.exec(parts.rawPrefix)?.[1];
}

/**
 * 番号付きリスト行の生プレフィックス（`  12. ` など）に書かれている数字を数値で返す。
 * ordered 以外の行、または数字を取り出せない場合は undefined。
 * 採番の開始値と、編集中の Enter による継続（「現在の数字 + 1」）の両方がこの値を使う。
 * 表示項番（採番結果）とは別物で、生テキストに書かれた利用者の入力を数値化したものを返す。
 */
export function markdownOrderedSourceNumber(parts: MarkdownLineParts): number | undefined {
  const digits = markdownOrderedSourceDigits(parts);
  if (digits === undefined) return undefined;
  return Number.parseInt(digits, 10);
}

/**
 * 項番（数字・枝番）を割り当てる最大のネストレベル。
 * デザインシステム「箇条書きリスト」の「第 1 階層は 1. 2. 3.、第 2 階層は 1-1. 1-2.、
 * 第 3 階層以降は●○を交互」に合わせ、level 0〜1 だけへ項番を割り当てる。
 * これより深い階層には箇条書きマーカー（bulletMarkerFor）を割り当てる。
 * ネストの上限（BULLET_MAX_LEVEL）とは意味が違うため、定数を分けている。
 */
export const ORDERED_LABEL_MAX_LEVEL = 1;

/**
 * その階層のカウンタが未設定のときの開始値。
 * 第 1 階層は、行に書かれた数字を尊重する（`5. 項目` は `5.` から始まる）。CommonMark の
 * `start` 属性と同じ慣習で、利用者が番号を途中から始める手段でもある。
 * 第 2 階層は、デザインシステムの counter-reset（子のカウンタは親ごとに 1 へ戻る）と
 * 同じく必ず 1 から始める。
 */
function initialCounterFor(line: MarkdownLineParts, level: number): number {
  if (level === 0) return markdownOrderedSourceNumber(line) ?? 1;
  return 1;
}

/**
 * 番号付きリスト行の表示用接頭辞を、行の配列 index をキーに返す。
 * 返すのは項番（"1." / "1-1."）だけでなく、項番を割り当てない第 3 階層以降では
 * 箇条書きマーカーの文字（"•"）である。呼び出し側はこの文字列をそのまま描画するため、
 * 表示 4 経路（非編集表示・編集 DOM・コメント本文・Canvas 出力）は自動で一致する。
 * `buildMarkdownSourceMap` の行配列を渡せば、キーは `lineIndex` と一致する。
 *
 * 規則:
 * 1. カウンタは階層ごとのスタックで持つ。
 * 2. level が ORDERED_LABEL_MAX_LEVEL より深い ordered 行には箇条書きマーカーを割り当て、
 *    カウンタは一切触らない。マーカー行を挟んでも祖先の採番はその行をまたいで続く。
 * 3. 項番を割り当てる行では、その level のカウンタが未設定なら initialCounterFor の開始値を、
 *    設定済みなら +1 を使う。より深い階層のカウンタは破棄する。
 * 4. ordered 以外の行（blank・plain・heading・bullet・quote・rule）が来たら、すべてのカウンタを破棄する。
 * 5. 表示項番は祖先のカウンタをハイフンで連結し、末尾にピリオドを付ける。
 * 6. 祖先のカウンタが無い状態で第 2 階層が現れた場合は、欠けている祖先を 1 として補う。
 *
 * 1 走査 O(n) で、行数に対して線形時間で終わる。
 */
export function assignOrderedListNumbers(
  lines: readonly MarkdownLineParts[]
): ReadonlyMap<number, string> {
  const numbers = new Map<number, string>();
  // 階層ごとのカウンタ（index = level）。未設定の階層は undefined。
  let counters: (number | undefined)[] = [];
  for (const [index, line] of lines.entries()) {
    if (line.kind !== "ordered") {
      counters = [];
      continue;
    }
    const level = line.level;
    if (level > ORDERED_LABEL_MAX_LEVEL) {
      // 第 3 階層以降はマーカー。カウンタを触らないため、この行を挟んでも親の採番は続く。
      numbers.set(index, bulletMarkerFor(level));
      continue;
    }
    // 欠けている祖先は 1 で補う（`  1. 子` から始まる本文でも `1-1.` になる）。
    for (let ancestor = 0; ancestor < level; ancestor += 1) {
      if (counters[ancestor] === undefined) counters[ancestor] = 1;
    }
    const current = counters[level];
    counters[level] = current === undefined ? initialCounterFor(line, level) : current + 1;
    // より深い階層のカウンタは破棄する（次に深い階層が現れたら、その階層の開始値から数え直す）。
    counters.length = level + 1;
    numbers.set(index, `${counters.slice(0, level + 1).join("-")}.`);
  }
  return numbers;
}

/**
 * 正規表現の量指定子（`#{1,6}`）が保証する個数を、型アサーションを使わずに
 * MarkdownHeadingLevel へ絞り込むためのマッピング。範囲外は undefined を返す。
 */
export function markdownHeadingLevel(count: number): MarkdownHeadingLevel | undefined {
  switch (count) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return 3;
    case 4:
      return 4;
    case 5:
      return 5;
    case 6:
      return 6;
    default:
      return undefined;
  }
}

//------------------------------------------------------------------------------
// リンク分割（表示テキスト付きリンク・素 URL）
//------------------------------------------------------------------------------

/** 文字列が http / https の URL かどうか（リンク化・遷移の許可判定）。 */
export function isHttpUrl(url: string): boolean {
  if (typeof url !== "string" || url.length === 0 || url.length > 8192) return false;
  try {
    const protocol = new URL(url).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

export interface LinkSegment {
  readonly type: "text" | "link";
  readonly value: string;
  /** type==="link" のときのみ設定される、遷移先 URL（検証済み）。 */
  readonly url?: string | undefined;
  /**
   * このセグメントが消費した元テキスト（省略時は value と同じ）。
   * 表示テキスト付きリンク（[テキスト](URL)）では value=表示テキスト、raw=元の記法全体となり、
   * findUrlTokenAt の文字オフセット計算は raw 基準で行う。
   */
  readonly raw?: string | undefined;
}

// 表示テキスト付きリンク（Markdown 形式 [テキスト](URL)）の開始部分（[テキスト]( まで）を
// 検出する正規表現。リンク付き HTML の貼り付け（use-object-text-editor の onPaste）で保持した
// 表示テキストを非編集表示でリンクとして描画するために使う。
// URL の終端は正規表現の貪欲一致に任せると、同一行の後続 )（隣接リンクや (メモ) 等）まで
// 飲み込んでしまうため、findMarkdownLinkUrlEnd が「括弧深度 0 で閉じる )」を走査して確定する。
// これにより URL 内で均衡した括弧（例: /wiki/Function_(mathematics)）は許容しつつ、
// リンク外側の文字を URL に取り込まない。
const MARKDOWN_LINK_START_REGEX = /\[((?:\\[\\[\]]|[^\]\\\n])+)\]\(/gu;

/**
 * "](" の直後（start）から走査し、URL の終端となる閉じ括弧の位置を返す。
 * URL 内で開いた ( は深度で対応させ、深度 0 で現れた ) を delimiter とする。
 * 空白・改行に出会った場合は Markdown リンクとして成立しないため null を返す。
 */
function findMarkdownLinkUrlEnd(text: string, start: number): number | null {
  let depth = 0;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i]!;
    if (ch === "(") {
      depth += 1;
      continue;
    }
    if (ch === ")") {
      if (depth === 0) {
        return i;
      }
      depth -= 1;
      continue;
    }
    if (/\s/u.test(ch)) {
      return null;
    }
  }
  return null;
}

/** [テキスト](URL) のテキスト側エスケープ（\[ \] \\）を元の文字へ戻す */
function unescapeLinkLabel(label: string): string {
  return label.replace(/\\([\\[\]])/gu, "$1");
}

// http(s):// で始まり、URL 本体として扱う文字の連続を URL 候補とみなす。末尾の約物・不均衡な
// 閉じ括弧は trimUrlTrailing で URL から切り離す。
// 「URL 本体として扱わない境界文字」＝空白に加え、日本語の助詞・約物（ひらがな ぀-ゟ /
// カタカナ ゠-ヿ / CJK 記号・句読点 　-〿 / 全角・半角形 ＀-￯）を除外する。
// これにより空白の無い日本語文（例: "詳細はhttps://example.comです"）で "です" まで取り込む過剰検出を防ぐ。
// CJK 統合漢字（一-鿿）は日本語パスの URL（例: /wiki/日本語）を壊さないよう URL 本体として許可する。
const URL_REGEX = /https?:\/\/[^\s　-〿぀-ゟ゠-ヿ＀-￯]+/giu;

// URL 末尾に紛れ込みやすい句読点（この集合は無条件で URL から切り離す）。
// インライン装飾のマーカー（`*` / `~` / `` ` ``）も含める。URL_REGEX は空白と日本語約物だけを
// 境界にするため、`~~https://example.com~~` の閉じマーカーまで URL 本体へ取り込んでしまい、
// 装飾が閉じられないうえ href にもマーカーが残る。末尾約物と同じ扱いで切り離す。
const TRAILING_PUNCTUATION = new Set([
  ".",
  ",",
  ";",
  ":",
  "!",
  "?",
  "、",
  "。",
  "，",
  "．",
  "*",
  "~",
  "`",
]);
// 閉じ括弧・引用符と、その対応する開き括弧。URL 内に対応する開きが無い（不均衡な）場合のみ切り離す。
const CLOSERS: Readonly<Record<string, string>> = {
  ")": "(",
  "]": "[",
  "}": "{",
  "）": "（",
  "」": "「",
  "』": "『",
  "”": "“",
  "’": "‘",
};

const OPENERS = new Set(Object.values(CLOSERS));

/**
 * URL 候補の末尾に紛れ込んだ約物・閉じ括弧を切り離し、URL 本体と末尾テキストへ分ける。
 * 例: "https://example.com/foo)." → { url: "https://example.com/foo", trailing: ")." }
 * ただし "https://ja.wikipedia.org/wiki/Foo_(bar)" のように URL 内で括弧が均衡していれば残す。
 *
 * 空白の無い日本語文（例: "参考(https://example.com/x)です"）でも URL を過剰に取り込まないよう、
 * 左から走査して「対応する開き括弧の無い閉じ括弧」が現れた位置で URL を打ち切る。その後、末尾の
 * 句読点を剥がす。
 */
function trimUrlTrailing(raw: string): { url: string; trailing: string } {
  const stack: string[] = [];
  let cut = raw.length;
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i]!;
    if (OPENERS.has(ch)) {
      stack.push(ch);
      continue;
    }
    const opener = CLOSERS[ch];
    if (opener !== undefined) {
      if (stack.length > 0 && stack[stack.length - 1] === opener) {
        stack.pop();
      } else {
        // 対応する開きの無い閉じ括弧 → ここより後ろは URL に含めない。
        cut = i;
        break;
      }
    }
  }
  let end = cut;
  while (end > 0 && TRAILING_PUNCTUATION.has(raw[end - 1]!)) {
    end -= 1;
  }
  return { url: raw.slice(0, end), trailing: raw.slice(end) };
}

/**
 * index 位置（Markdown 開始候補の "[" の位置）が、直前から続く素の URL トークンの途中か。
 * 直前の空白までさかのぼった接頭辞の中で「index まで連続する最後の http(s):// 開始位置」を
 * 探し、そこからの部分文字列が有効な URL であれば、この "[" は bare URL の path / query の
 * 一部とみなして Markdown リンクとして先取りしない
 *（例: https://example.com/docs/[guide](draft) を途中で切断しない）。
 * 接頭辞の先頭一致ではなく最後の http(s):// を探すのは、「詳細はhttps://...」のように
 * 空白を挟まず URL が始まるケースや、「参考(https://...」のように括弧に続くケースでも
 * bare URL 内と判定できるようにするため。
 */
// isInsideBareUrlPrefix 用: 素の URL 本体（URL_REGEX と同じ文字集合）の先頭一致判定。
const BARE_URL_PREFIX_REGEX = /^https?:\/\/[^\s　-〿぀-ゟ゠-ヿ＀-￯]+/iu;

function isInsideBareUrlPrefix(text: string, index: number): boolean {
  let tokenStart = index;
  while (tokenStart > 0 && !/\s/u.test(text[tokenStart - 1]!)) {
    tokenStart -= 1;
  }
  const prefix = text.slice(tokenStart, index);
  const httpStart = Math.max(prefix.lastIndexOf("http://"), prefix.lastIndexOf("https://"));
  if (httpStart < 0) {
    return false;
  }
  // URL 本体が index の直前まで途切れず連続していることを確認する。途中で URL が
  // 終わっている場合（隣接する Markdown リンクの ")" の後や、CJK 文字・末尾約物で
  // 打ち切られる場合）は bare URL の内側ではないため、Markdown 判定を妨げない。
  const candidate = prefix.slice(httpStart);
  const body = BARE_URL_PREFIX_REGEX.exec(candidate);
  if (body === null || body[0].length !== candidate.length) {
    return false;
  }
  const { url } = trimUrlTrailing(candidate);
  if (url.length !== candidate.length) {
    return false;
  }
  return isHttpUrl(url);
}

/** 素の URL 検出のみでテキストをセグメント列へ分割する（従来の分割処理） */
function splitBareUrlSegments(text: string): readonly LinkSegment[] {
  const segments: LinkSegment[] = [];
  let lastIndex = 0;
  URL_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const raw = match[0];
    const start = match.index;
    if (start > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, start) });
    }
    const { url, trailing } = trimUrlTrailing(raw);
    if (isHttpUrl(url)) {
      segments.push({ type: "link", value: url, url });
    } else {
      segments.push({ type: "text", value: url });
    }
    if (trailing.length > 0) {
      segments.push({ type: "text", value: trailing });
    }
    lastIndex = start + raw.length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }
  return segments;
}

/**
 * 本文全体（改行含む）を、通常テキストと URL リンクのセグメント列へ分割する。
 * まず表示テキスト付きリンク（[テキスト](URL)）を抽出し、残りのテキストへ素 URL 検出を
 * 適用する（リンク内 URL の二重リンク化を防ぐ）。
 * URL でも http/https でなければ text セグメントへ落とす（安全側）。
 */
export function splitTextIntoLinkSegments(text: string): readonly LinkSegment[] {
  const segments: LinkSegment[] = [];
  let lastIndex = 0;
  MARKDOWN_LINK_START_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MARKDOWN_LINK_START_REGEX.exec(text)) !== null) {
    if (isInsideBareUrlPrefix(text, match.index)) {
      // 素の URL トークンの途中に現れた "[...](" は Markdown として先取りせず、
      // 後段の splitBareUrlSegments（素 URL 検出）に 1 本の URL として任せる。
      continue;
    }
    const start = match.index;
    if (start < lastIndex) {
      // 直前に確定したリンク範囲の内側での誤マッチ（URL 内の "[x](" 等）は読み飛ばす。
      continue;
    }
    const urlStart = MARKDOWN_LINK_START_REGEX.lastIndex;
    const urlEnd = findMarkdownLinkUrlEnd(text, urlStart);
    if (urlEnd === null) {
      // URL 終端（深度 0 の )）が確定しない場合は Markdown リンクとして扱わず、
      // この開始位置は素のテキスト（後段の素 URL 検出）に任せる。
      continue;
    }
    const raw = text.slice(start, urlEnd + 1);
    const label = unescapeLinkLabel(match[1] ?? "");
    // 記法内の URL は括弧で明示的に区切られているため、素 URL 検出と異なり末尾約物の
    // 切り離し（trimUrlTrailing）は行わず、括弧内をそのまま URL として尊重する
    //（Markdown の一般挙動と一致。raw = 記法全体となり原文との連続オフセット対応も保たれる）。
    const url = text.slice(urlStart, urlEnd);
    if (start > lastIndex) {
      segments.push(...splitBareUrlSegments(text.slice(lastIndex, start)));
    }
    if (isHttpUrl(url)) {
      segments.push({ type: "link", value: label, url, raw });
    } else {
      // http(s) でない URL はリンク化せず、記法全体をテキストのまま残す（安全側。
      // 素 URL 検出へも戻さないため、href 内の http(s) 断片が部分リンク化されることもない）。
      segments.push({ type: "text", value: raw });
    }
    lastIndex = start + raw.length;
    // URL 内の "[x](" などを次の開始位置として拾わないよう、走査位置をリンク終端へ進める。
    MARKDOWN_LINK_START_REGEX.lastIndex = lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push(...splitBareUrlSegments(text.slice(lastIndex)));
  }
  return coalesceTextSegments(segments);
}

// 連続する text セグメントを 1 つに結合する（URL 末尾トリムと URL 後テキストが別セグメントになるのを畳む）。
function coalesceTextSegments(segments: readonly LinkSegment[]): readonly LinkSegment[] {
  const merged: LinkSegment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (segment.type === "text" && last && last.type === "text") {
      merged[merged.length - 1] = { type: "text", value: last.value + segment.value };
    } else {
      merged.push(segment);
    }
  }
  return merged;
}

/**
 * 全文中の文字オフセット位置に重なる URL（リンクセグメント）を返す。無ければ null。
 * 編集中（contentEditable）に Ctrl/Cmd+クリックした位置の URL を判定するのに使う。
 * オフセットは編集中の元テキスト基準のため、表示テキスト付きリンクは raw（記法全体）の長さで数える。
 */
export function findUrlTokenAt(text: string, offset: number): string | null {
  if (offset < 0 || offset > text.length) return null;
  let pos = 0;
  const segments = splitTextIntoLinkSegments(text);
  for (const [index, segment] of segments.entries()) {
    const segEnd = pos + (segment.raw ?? segment.value).length;
    // 終端は inclusive（リンク末尾の直後のキャレットでも開ける）としつつ、直後に別リンクが
    // 隣接する場合の共有境界だけは後ろ側のリンクへ譲る（前側リンクが境界を吸うと、
    // [A](url)[B](url) の 2 本目先頭を Ctrl+クリックしたとき A が開いてしまう）。
    const sharedBoundaryWithNextLink = segments[index + 1]?.type === "link" && offset === segEnd;
    if (
      segment.type === "link" &&
      segment.url &&
      offset >= pos &&
      offset <= segEnd &&
      !sharedBoundaryWithNextLink
    ) {
      return segment.url;
    }
    pos = segEnd;
  }
  return null;
}

//------------------------------------------------------------------------------
// raw offset ↔ 編集時可視区間 の対応表
//------------------------------------------------------------------------------

/** 半開区間 `[start, end)` の raw offset 範囲。 */
export interface RawOffsetRange {
  readonly start: number;
  readonly end: number;
}

/** 1 行ぶんの raw offset 対応表。 */
export interface MarkdownSourceLine extends MarkdownLineParts {
  /** 0 始まりの行番号。 */
  readonly lineIndex: number;
  /** 行頭（rawPrefix の先頭）の raw offset。 */
  readonly rawStart: number;
  /** 可視本文（content）の raw 開始 offset（= rawStart + rawPrefix.length）。 */
  readonly contentStart: number;
  /** 可視本文（content）の raw 終端 offset（半開区間）。 */
  readonly contentEnd: number;
  /** task 行の `[ ]` / `[x]` / `[X]` 内の文字を指す絶対 raw offset。 */
  readonly checkMarkOffset?: number | undefined;
}

/** 本文全体の raw offset 対応表。 */
export interface MarkdownSourceMap {
  readonly text: string;
  readonly lines: readonly MarkdownSourceLine[];
  /**
   * 番号付きリスト行の表示用接頭辞。キーは `lineIndex`。項番（"1." / "1-1."）のほか、
   * 項番を割り当てない第 3 階層以降では箇条書きマーカーの文字（"•"）が入る
   * （assignOrderedListNumbers を参照）。
   * 接頭辞は行単体では決まらない（前後の行に依存する）ため、構築時に一度だけ採番して持つ。
   */
  readonly orderedNumbers: ReadonlyMap<number, string>;
  /**
   * 表示・出力が使う「複数行をまとめた」並び。`lines` を置き換えるものではなく、上に重ねる層。
   * すべての行がちょうど 1 つのブロックへ属し、表を含まない本文では `lines` と 1:1 の
   * `kind: "line"` だけになる（既存の見た目は変わらない）。
   * 編集 DOM・部分書式・Undo / Redo は従来どおり `lines` だけを見るため、表の行も raw offset
   * 座標系は 1 文字も動かない。
   */
  readonly blocks: readonly MarkdownBlock[];
}

/**
 * 生 Markdown 本文を行へ分割し、各行の記法プレフィックスと可視本文の raw offset 範囲を返す。
 * 行分割は既存パーサと同じ `/\r?\n/`。改行 1 文字ぶんは次行の rawStart へ含めない
 * （改行そのものは可視本文ではないため、部分書式の run を作らない区間になる）。
 */
export function buildMarkdownSourceMap(text: string): MarkdownSourceMap {
  const lines: MarkdownSourceLine[] = [];
  const rawLines = text.split(/\r?\n/);
  let rawStart = 0;
  for (const [lineIndex, rawLine] of rawLines.entries()) {
    const parts = parseMarkdownLineParts(rawLine);
    const contentStart = rawStart + parts.rawPrefix.length;
    lines.push({
      ...parts,
      lineIndex,
      rawStart,
      contentStart,
      contentEnd: contentStart + parts.content.length,
      ...(parts.kind === "task"
        ? { checkMarkOffset: rawStart + parts.rawPrefix.lastIndexOf("[") + 1 }
        : {}),
    });
    // CRLF でも `text.split(/\r?\n/)` の各要素長 + 改行 1 文字とはずれ得るため、
    // 元テキスト上の実際の改行長を測って進める（CR は行末に残らない）。
    const consumed = rawStart + rawLine.length;
    rawStart = consumed + newlineLengthAt(text, consumed);
  }
  return {
    blocks: detectMarkdownBlocks(text, lines),
    lines,
    orderedNumbers: assignOrderedListNumbers(lines),
    text,
  };
}

/**
 * `offset` が指す task 行のチェック印だけを切り替える。対象外なら null を返す。
 * 置換は常に 1 文字から 1 文字なので、本文長と既存の raw offset は変わらない。
 */
export function toggleMarkdownTaskAt(text: string, offset: number): string | null {
  if (!Number.isInteger(offset) || offset < 0 || offset >= text.length) return null;
  const line = buildMarkdownSourceMap(text).lines.find(
    (candidate) => candidate.kind === "task" && candidate.checkMarkOffset === offset
  );
  if (!line) return null;
  const mark = text[offset];
  if (mark !== " " && mark !== "x" && mark !== "X") return null;
  const replacement = mark === " " ? "x" : " ";
  return `${text.slice(0, offset)}${replacement}${text.slice(offset + 1)}`;
}

/** `text` の `index` 位置にある改行の長さ（CRLF=2 / LF=1 / それ以外=0）。 */
function newlineLengthAt(text: string, index: number): number {
  if (text.startsWith("\r\n", index)) return 2;
  if (text.startsWith("\n", index)) return 1;
  return 0;
}

/**
 * 部分書式の run を作ってよい「可視本文」の raw 区間一覧を返す。
 * 非表示・編集不可の記法プレフィックス（見出しの `## `・箇条書きの `  - `）と改行は含めない。
 */
export function markdownVisibleRanges(map: MarkdownSourceMap): readonly RawOffsetRange[] {
  const ranges: RawOffsetRange[] = [];
  for (const line of map.lines) {
    if (line.contentEnd > line.contentStart) {
      ranges.push({ end: line.contentEnd, start: line.contentStart });
    }
  }
  return ranges;
}

/**
 * raw 区間 `[start, end)` を可視本文へ切り出す（行ごとに分割される）。
 * 範囲パネルの操作は、この結果の区間だけへ書式を適用する。
 */
export function clipRangeToMarkdownVisible(
  map: MarkdownSourceMap,
  start: number,
  end: number
): readonly RawOffsetRange[] {
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  const clipped: RawOffsetRange[] = [];
  for (const line of map.lines) {
    const s = Math.max(lo, line.contentStart);
    const e = Math.min(hi, line.contentEnd);
    if (e > s) clipped.push({ end: e, start: s });
  }
  return clipped;
}

//------------------------------------------------------------------------------
// ブロック層（表 = 複数行で 1 つのまとまり）
//------------------------------------------------------------------------------

/**
 * 表示・出力が扱う本文のブロック。
 * `line` は従来どおりの 1 行（`lines[lineIndex]`）、`table` は「ヘッダ行 + 区切り行 + ボディ行」を
 * 1 つにまとめた GFM 基本形の表、`code` は「開始フェンス行 + 中身の行 + 終了フェンス行」を 1 つに
 * まとめたコードブロック、`callout` は「開始フェンス行 + 本文行 + 終了フェンス行」を 1 つにまとめた
 * Connect Note 互換の callout。行の分類（`MarkdownLineKind`）は変更せず、表・コードブロック・
 * callout の行も従来どおりの分類のまま残す。どのブロックかはこのブロック層でだけ判定する。
 */
export type MarkdownBlock =
  | { readonly kind: "line"; readonly lineIndex: number }
  | { readonly kind: "table"; readonly table: MarkdownTable }
  | { readonly kind: "code"; readonly code: MarkdownCodeBlock }
  | { readonly kind: "callout"; readonly callout: MarkdownCallout };

/** callout の種別（`:::note info` の type。未知・省略は既定の `note`）。 */
export type MarkdownCalloutType = "info" | "warn" | "alert" | "note";

/** callout の本文を構成する内側のブロック（`line` だけで構成する）。 */
export interface MarkdownCalloutBodyLine {
  readonly kind: "line";
  readonly lineIndex: number;
}

/**
 * Connect Note 互換の callout（`:::note info` … `:::`）。
 * 開始行・終了行・本文行は従来どおりの行分類のまま残し、囲みであることはこの型でだけ表す。
 */
export interface MarkdownCallout {
  readonly type: MarkdownCalloutType;
  /** 開始フェンス行の index。 */
  readonly openLineIndex: number;
  /** 終了フェンス行の index。 */
  readonly closeLineIndex: number;
  /** タイトルの raw 範囲（半開区間）。タイトルが無ければ start === end。 */
  readonly titleStart: number;
  readonly titleEnd: number;
  /**
   * 本文を構成する内側のブロック。line だけで構成する（表・コードブロックは作らない）。
   * 本文の行 index はここだけが正本で、別フィールドへ重複して持たない
   * （2 か所に持つと、解析・描画・ソースマップで参照先が割れたときに検出できない）。
   * 不変条件: body の lineIndex は openLineIndex より大きく closeLineIndex より小さい
   * 連続した昇順で、長さは closeLineIndex - openLineIndex - 1 と一致する。
   */
  readonly body: readonly MarkdownCalloutBodyLine[];
}

/**
 * バッククォート 3 個以上のフェンスで囲んだコードブロック。
 * 中身の行は Markdown として一切解釈せず、生テキストのまま等幅で表示する。
 */
export interface MarkdownCodeBlock {
  /** 開始フェンス行の index。 */
  readonly fenceStartLineIndex: number;
  /** 終了フェンス行の index。閉じずに本文が終わった場合は undefined。 */
  readonly fenceEndLineIndex?: number | undefined;
  /** 中身の行 index（フェンス行を含まない）。 */
  readonly contentLineIndexes: readonly number[];
  /** info string（言語名）。表示には使わないが、将来のハイライトのために保持する。 */
  readonly info: string;
}

/** 表の列の寄せ（区切り行の `:` で指定する）。 */
export type MarkdownTableAlign = "left" | "center" | "right";

/** 表の 1 セル。中身は raw 範囲で表し、表示テキスト・リンク・装飾は表示時に範囲から導く。 */
export interface MarkdownTableCell {
  /** セル本文の raw 範囲（前後の空白を除いた半開区間）。 */
  readonly start: number;
  readonly end: number;
  /**
   * 本文に対応するセルが無く、列数をそろえるために補ったセルか。
   * 補ったセルは `start === end === その行の末尾 raw offset` のゼロ幅範囲を持つ。
   */
  readonly synthesized?: boolean | undefined;
}

/** 表の 1 行（ヘッダ行またはボディ行）。 */
export interface MarkdownTableRow {
  readonly lineIndex: number;
  readonly cells: readonly MarkdownTableCell[];
}

/** GFM 基本形の表。 */
export interface MarkdownTable {
  readonly headerLineIndex: number;
  readonly delimiterLineIndex: number;
  readonly header: MarkdownTableRow;
  /** ボディ行（ヘッダ + 区切り行だけの表では空）。各行のセル数は `columnCount` にそろえてある。 */
  readonly body: readonly MarkdownTableRow[];
  readonly aligns: readonly MarkdownTableAlign[];
  /** 列数（ヘッダ行のセル数）。 */
  readonly columnCount: number;
}

/**
 * セル 1 列あたりの左右 padding 合計（em）。デザインシステム「テーブルのセルの左右 padding 1em」× 2。
 * DOM（`padding-inline: 1em`）と Canvas 出力が共有する。
 */
export const TABLE_CELL_PADDING_EM = 2;

/**
 * 列の最小幅（em）。padding 2em + 全角 2 文字ぶん（2em）で、最低限 2 文字は読める幅にする。
 * 使用可能幅に収まらない表を比例縮小するときの下限。
 */
export const TABLE_MIN_COLUMN_EM = 4;

/**
 * 表の外枠線の太さ（px）。DOM の `border: 1px` と同じで、使用可能幅から左右 2 本ぶんを差し引く
 * （`markdownTableAvailableEm`）。差し引かないと DOM と Canvas の切り取り位置がずれる。
 */
export const TABLE_BORDER_PX = 1;

/** 表の行の最低高さ（em）。デザインシステム「テーブルの行の最低高さ 2em」。 */
export const TABLE_ROW_MIN_HEIGHT_EM = 2;

/**
 * 表のセル本文 1 行あたりの高さ（em）。DOM の既定行送り（`line-height: 1.5`）と同じ値で、
 * 折り返した行数から行の必要高さを見積もるときに使う（`resolveTableObjectRowHeights`）。
 *
 * 1 行に収まるセルでは最小行高（{@link TABLE_ROW_MIN_HEIGHT_EM} = 2em）のほうが大きいため、
 * 折り返しの無い既存の表の行高は変わらない。
 */
export const TABLE_ROW_LINE_EM = 1.5;

/**
 * 表の区切り行のセル（前後の空白を除いたもの）: 省略可能な `:` + 1 個以上の `-` + 省略可能な `:`。
 * 単独の `---` は水平線（rule）として分類されて plain にならないため、区切り行になることはない。
 */
export const TABLE_DELIMITER_CELL = /^:?-+:?$/;

/**
 * 本文の行列を、コードブロック・callout・表（いずれも複数行）と単独行のブロック列へまとめる。
 * すべての行がちょうど 1 つのブロックへ属する。フェンス（```` ``` ````）もコンテナ記法（`:::`）も
 * パイプ（`|`）も 1 つも含まない本文は走査を省き、全行を `line` ブロックにする（どの記法も
 * 使っていない既存本文の構築コストを増やさない）。
 *
 * **検出順は「コードブロック → callout → 表」で固定する。**
 * - コードブロックを先に見るのは、フェンスの中に書いた `:::note` や `| a | b |` を
 *   callout・表として誤検出しないため（フェンスの中身は「そのまま見せる」のが記法の意味）。
 * - callout を表より先に見るのは、callout の本文行を表として検出しないため
 *   （callout の中へ表・コードブロックを入れ子にしない、という仕様を検出順で機械的に保証する）。
 */
function detectMarkdownBlocks(
  text: string,
  lines: readonly MarkdownSourceLine[]
): readonly MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const hasFence = text.includes("```");
  const hasContainer = text.includes(":::");
  const hasPipe = text.includes("|");
  if (!hasFence && !hasContainer && !hasPipe) {
    for (const line of lines) blocks.push({ kind: "line", lineIndex: line.lineIndex });
    return blocks;
  }
  // callout の終了行は 1 回の走査で表にしてから引く（開始行ごとに末尾まで探さない）。
  const calloutCloseLines = hasContainer ? buildCalloutCloseLineTable(text, lines) : null;
  let index = 0;
  while (index < lines.length) {
    const code = hasFence ? parseMarkdownCodeBlockAt(text, lines, index) : null;
    if (code !== null) {
      blocks.push({ code, kind: "code" });
      // 終了フェンスが無い場合は本文の最後までがコードブロック（CommonMark と同じ）。
      index = (code.fenceEndLineIndex ?? lines.length - 1) + 1;
      continue;
    }
    const callout =
      calloutCloseLines === null
        ? null
        : parseMarkdownCalloutAt(text, lines, index, calloutCloseLines);
    if (callout !== null) {
      blocks.push({ callout, kind: "callout" });
      index = callout.closeLineIndex + 1;
      continue;
    }
    const table = hasPipe ? parseMarkdownTableAt(text, lines, index) : null;
    if (table === null) {
      blocks.push({ kind: "line", lineIndex: index });
      index += 1;
      continue;
    }
    blocks.push({ kind: "table", table });
    index = table.delimiterLineIndex + 1 + table.body.length;
  }
  return blocks;
}

// 開始フェンス: 行頭からバッククォート 3 個以上 + info string（バッククォートを含まない）。
// 行頭の字下げは認めない（箇条書きのネスト解釈と衝突するため。見出し・水平線と同じ規則）。
const CODE_FENCE_OPEN = /^(`{3,})([^`]*)$/;

// 終了フェンス候補: バッククォートだけからなる行（末尾の半角スペースは無視する）。
// 実際に閉じるかどうかは、開始フェンスと同じ個数以上であるかで決める。
const CODE_FENCE_CLOSE = /^(`{3,}) *$/;

/**
 * `index` 行目を開始フェンスとするコードブロックを解析する。成立しなければ null。
 *
 * - 開始フェンス: `CODE_FENCE_OPEN` に一致する行。info string は前後の空白を除いて保持する。
 * - 終了フェンス: 開始フェンスと**同じ個数以上**のバッククォートだけからなる行。
 * - 終了フェンスが現れないまま本文が終わった場合は、本文の最後までをコードブロックとする。
 *
 * 行の判定には行分類済みの `content` ではなく生の行テキストを使う。コードブロックの中の
 * `- item` は行分類が `bullet` になり `content` からマーカーが落ちるため、フェンスの探索も
 * 中身の切り出しも生テキストで行う必要がある。
 */
function parseMarkdownCodeBlockAt(
  text: string,
  lines: readonly MarkdownSourceLine[],
  index: number
): MarkdownCodeBlock | null {
  const openLine = lines[index];
  if (openLine === undefined) return null;
  const open = CODE_FENCE_OPEN.exec(rawLineTextOf(text, openLine));
  if (open === null || open[1] === undefined || open[2] === undefined) return null;
  const fenceLength = open[1].length;
  const contentLineIndexes: number[] = [];
  for (let lineIndex = index + 1; lineIndex < lines.length; lineIndex += 1) {
    const close = CODE_FENCE_CLOSE.exec(rawLineTextOf(text, lines[lineIndex]!));
    if (close !== null && (close[1]?.length ?? 0) >= fenceLength) {
      return {
        contentLineIndexes,
        fenceEndLineIndex: lineIndex,
        fenceStartLineIndex: index,
        info: open[2].trim(),
      };
    }
    contentLineIndexes.push(lineIndex);
  }
  return { contentLineIndexes, fenceStartLineIndex: index, info: open[2].trim() };
}

/** 1 行の生テキスト（改行を含まない）を元テキストから切り出す。 */
function rawLineTextOf(text: string, line: MarkdownSourceLine): string {
  return text.slice(line.rawStart, line.contentEnd);
}

/**
 * 行の生テキスト（改行を含まない）。ブロックが記法解釈を無効にしたい場合に使う。
 * 不変条件 `rawPrefix + content === 行` により、`rawStart` から `contentEnd` までを切り出せば
 * 行全体がそのまま得られる（`- item` なら `"- item"` を返す）。
 * 範囲外の `lineIndex` では空文字列を返す。
 */
export function markdownRawLineText(map: MarkdownSourceMap, lineIndex: number): string {
  const line = map.lines[lineIndex];
  if (line === undefined) return "";
  return rawLineTextOf(map.text, line);
}

/** `markdownCodeBlockAtLine` が返す、行のコードブロック内での役割。 */
export type MarkdownCodeBlockLineRole = "fence-start" | "content" | "fence-end";

/**
 * `lineIndex` の行が属するコードブロックと、その行の役割を返す。コードブロックの外なら undefined。
 * 非編集表示のキャレット写像（web の markdown-wysiwyg.ts）が、行を raw のまま描くかどうかを
 * 判定するために使う。
 */
export function markdownCodeBlockAtLine(
  map: MarkdownSourceMap,
  lineIndex: number
): { readonly code: MarkdownCodeBlock; readonly role: MarkdownCodeBlockLineRole } | undefined {
  for (const block of map.blocks) {
    if (block.kind !== "code") continue;
    const { code } = block;
    if (lineIndex === code.fenceStartLineIndex) return { code, role: "fence-start" };
    if (lineIndex === code.fenceEndLineIndex) return { code, role: "fence-end" };
    // 中身の行は開始フェンスの直後から連続するため、両端の比較だけで判定できる。
    const lastContent = code.contentLineIndexes[code.contentLineIndexes.length - 1];
    if (
      lastContent !== undefined &&
      lineIndex > code.fenceStartLineIndex &&
      lineIndex <= lastContent
    ) {
      return { code, role: "content" };
    }
  }
  return undefined;
}

//------------------------------------------------------------------------------
// callout（Connect Note 互換の `:::note`）
//------------------------------------------------------------------------------

// callout 開始: 行頭インデント + ::: + 任意空白 + note（直後に英字が続かない）+ 任意空白
//              + 省略可能な type + 任意空白 + 省略可能なタイトル。
// `:::notebook` を誤検出しないよう、note の直後に英字が続く行は開始行にしない。
// Connect Note 由来データに `::: note info` 表記が混在するため、::: と note の間の空白も許す。
const CALLOUT_OPEN_RE = /^[ \t]*:::[ \t]*note(?![A-Za-z])[ \t]*([A-Za-z]+)?[ \t]*(.*)$/u;

// callout 終了: ::: だけの行。NBSP（U+00A0）・全角空白（U+3000）が残っていても終端と認める
//              （Connect Note 由来の日本語本文では不可視の空白が残りやすい）。
//              この 2 文字は画面上で半角スペースと見分けが付かないため、必ず \u エスケープで書く
//              （直書きすると、後から読む人が文字集合を読み違える）。
const CALLOUT_CLOSE_RE = /^[ \t\u00a0\u3000]*:::[ \t\u00a0\u3000]*$/u;

/** 受理する callout の type（この集合に無い英字列は既定の `note` へ落とす）。 */
const CALLOUT_TYPES: readonly MarkdownCalloutType[] = ["info", "warn", "alert", "note"];

/**
 * `index` 行目を開始フェンスとする callout を解析する。成立しなければ null。
 *
 * - 終了行が見つからないまま本文が尽きた場合は callout にしない（Connect Note と同じ）。
 * - 本文中のコードフェンス（```` ``` ````）の内側にある `:::` は終端と見なさない。
 *   フェンスの開始・終了の判定はコードブロックの検出（`CODE_FENCE_OPEN` /
 *   `CODE_FENCE_CLOSE`）と同じ実装を共有し、2 つの記法でフェンスの受理範囲が割れないようにする。
 *   終了行の探索は `buildCalloutCloseLineTable` が作った表の参照 1 回で済ませる。
 * - type は大文字小文字を無視し、未知の英字列は既定の `note` へ落とす（その英字列は
 *   type の位置で消費するため、タイトルにはしない）。
 * - タイトルは type より後ろの残り全体で、`String.prototype.trim()` と同じ空白集合で
 *   前後を詰めた raw 範囲を持つ（末尾の空白を残すと `white-space: pre-wrap` の表示で
 *   幅・折り返し・キャレット位置が実際にずれる）。
 */
function parseMarkdownCalloutAt(
  text: string,
  lines: readonly MarkdownSourceLine[],
  index: number,
  closeLineTable: readonly number[]
): MarkdownCallout | null {
  const openLine = lines[index];
  if (openLine === undefined) return null;
  const openText = rawLineTextOf(text, openLine);
  const open = CALLOUT_OPEN_RE.exec(openText);
  if (open === null || open[2] === undefined) return null;
  const closeLineIndex = closeLineTable[index + 1] ?? -1;
  if (closeLineIndex < 0) return null;
  const typeWord = open[1]?.toLowerCase();
  const type = CALLOUT_TYPES.find((candidate) => candidate === typeWord) ?? "note";
  // `(.*)$` は行の残り全体に一致するため、その開始位置は「行長 − 残りの長さ」で求まる。
  const titleRawStart = openLine.rawStart + (openText.length - open[2].length);
  const title = trimmedRawRange(text, titleRawStart, titleRawStart + open[2].length);
  const body: MarkdownCalloutBodyLine[] = [];
  for (let lineIndex = index + 1; lineIndex < closeLineIndex; lineIndex += 1) {
    body.push({ kind: "line", lineIndex });
  }
  return {
    body,
    closeLineIndex,
    openLineIndex: index,
    titleEnd: title.end,
    titleStart: title.start,
    type,
  };
}

/**
 * 「各行以降で最初の callout 終了行（コードフェンスの内側にない `:::` だけの行）」を引ける表を、
 * 1 回の走査で作る。値は行 index で、その行以降に終了行が無ければ -1。長さは行数 + 1 で、
 * 末尾の 1 要素は「本文の終わり以降」を表す番兵（常に -1）。
 *
 * 開始行ごとに末尾まで探すと、閉じる `:::` が無い `:::note` を多く含む本文で行数の 2 乗に
 * 比例する。source map は非編集表示・キャレット写像・Canvas 出力・付箋のフォント自動調整で
 * 繰り返し作られるため、線形に保つ。
 *
 * **フェンスの状態を本文の先頭から追ってよい理由**: callout の開始候補になり得る行は必ず
 * 「コードブロックの外」である（`detectMarkdownBlocks` はコードブロックを先に 1 ブロックとして
 * 消費し、その中身を候補にしない）。開始候補の次の行から追い直した場合と、本文の先頭から
 * 追った場合とで、その開始候補より後のフェンス状態は必ず一致する。
 */
function buildCalloutCloseLineTable(
  text: string,
  lines: readonly MarkdownSourceLine[]
): readonly number[] {
  const isCloseLine: boolean[] = new Array<boolean>(lines.length).fill(false);
  // 追跡中のコードフェンスのマーカー長（フェンスの外では undefined）。
  let fenceLength: number | undefined;
  for (const [lineIndex, line] of lines.entries()) {
    const lineText = rawLineTextOf(text, line);
    if (fenceLength !== undefined) {
      const close = CODE_FENCE_CLOSE.exec(lineText);
      if (close !== null && (close[1]?.length ?? 0) >= fenceLength) fenceLength = undefined;
      continue;
    }
    const openFence = CODE_FENCE_OPEN.exec(lineText);
    if (openFence !== null && openFence[1] !== undefined) {
      fenceLength = openFence[1].length;
      continue;
    }
    if (CALLOUT_CLOSE_RE.test(lineText)) isCloseLine[lineIndex] = true;
  }
  // 後ろから前へ「その行以降で最初の終了行」を埋める。
  const table: number[] = new Array<number>(lines.length + 1).fill(-1);
  for (let lineIndex = lines.length - 1; lineIndex >= 0; lineIndex -= 1) {
    table[lineIndex] = isCloseLine[lineIndex] === true ? lineIndex : table[lineIndex + 1]!;
  }
  return table;
}

/**
 * raw 範囲 `[start, end)` から、`String.prototype.trim()` と同じ空白集合で前後を詰めた範囲を返す。
 * 正規表現の `[ \t]*` は NBSP・全角空白を読み飛ばさないため、タイトルの前後はここで詰める。
 * JavaScript の `\s` は trim が取り除く空白・改行の集合と一致する。
 */
function trimmedRawRange(
  text: string,
  start: number,
  end: number
): { readonly start: number; readonly end: number } {
  let s = start;
  let e = end;
  while (s < e && /\s/u.test(text[s]!)) s += 1;
  while (e > s && /\s/u.test(text[e - 1]!)) e -= 1;
  return { end: e, start: s };
}

/** `markdownCalloutAtLine` が返す、行の callout 内での役割。 */
export type MarkdownCalloutLineRole = "open" | "body" | "close";

/**
 * `lineIndex` の行が属する callout と、その行の役割を返す。callout の外なら undefined。
 * 非編集表示のキャレット写像（web の markdown-wysiwyg.ts）が、フェンス行とタイトルの
 * 写像先を決めるために使う。
 */
export function markdownCalloutAtLine(
  map: MarkdownSourceMap,
  lineIndex: number
): { readonly callout: MarkdownCallout; readonly role: MarkdownCalloutLineRole } | undefined {
  for (const block of map.blocks) {
    if (block.kind !== "callout") continue;
    const { callout } = block;
    if (lineIndex === callout.openLineIndex) return { callout, role: "open" };
    if (lineIndex === callout.closeLineIndex) return { callout, role: "close" };
    if (lineIndex > callout.openLineIndex && lineIndex < callout.closeLineIndex) {
      return { callout, role: "body" };
    }
  }
  return undefined;
}

/**
 * callout のタイトルの「画面に見える」表示テキスト（リンクは表示テキストへ縮約し、
 * 装飾マーカーは除く）。タイトル要素を作るかどうかの判定（表示文字が 1 文字以上あるか）と
 * プレビュー縮約が共有する。`\|` は畳まない（タイトルは表のセルではないため）。
 */
export function markdownCalloutTitleDisplayText(text: string, callout: MarkdownCallout): string {
  return markdownRangeDisplaySegments(text, callout.titleStart, callout.titleEnd)
    .map((segment) => segment.text)
    .join("");
}

/**
 * callout がタイトル要素を持つか。raw 範囲の有無ではなく**表示文字が 1 文字以上あるか**で決める。
 * `:::note info ****` のように raw は空でないが表示文字が 0 になるタイトルで、中身の無い
 * タイトル要素と 1 行分の高さができてしまうのを防ぐ。
 */
export function markdownCalloutHasTitle(text: string, callout: MarkdownCallout): boolean {
  return markdownCalloutTitleDisplayText(text, callout).length > 0;
}

/**
 * `index` 行目をヘッダ行とする表を解析する。成立しなければ null。
 *
 * - ヘッダ行: エスケープされていないパイプを 1 個以上含む plain 行。
 * - 区切り行: ヘッダ行の直後の plain 行で、各セルが `TABLE_DELIMITER_CELL` に一致し、
 *   セル数がヘッダ行と一致するもの。
 * - ボディ行: 区切り行の次から、パイプを含む plain 行が続く限り。パイプを含まない行・空行・
 *   記法プレフィックスを持つ行（箇条書き・見出し等）・本文の終わりで表は閉じる。
 * - ボディ行のセル数がヘッダより少ないときは空セルで埋め、多いときは切り捨てる（GFM と同じ）。
 */
function parseMarkdownTableAt(
  text: string,
  lines: readonly MarkdownSourceLine[],
  index: number
): MarkdownTable | null {
  const headerLine = lines[index];
  const delimiterLine = lines[index + 1];
  if (headerLine === undefined || delimiterLine === undefined) return null;
  const headerCells = splitTableRowCells(text, headerLine);
  if (headerCells === null) return null;
  const delimiterCells = splitTableRowCells(text, delimiterLine);
  if (delimiterCells === null || delimiterCells.length !== headerCells.length) return null;
  const aligns: MarkdownTableAlign[] = [];
  for (const cell of delimiterCells) {
    const raw = text.slice(cell.start, cell.end);
    if (!TABLE_DELIMITER_CELL.test(raw)) return null;
    const leading = raw.startsWith(":");
    const trailing = raw.endsWith(":");
    aligns.push(leading && trailing ? "center" : trailing ? "right" : "left");
  }
  const columnCount = headerCells.length;
  const body: MarkdownTableRow[] = [];
  for (let lineIndex = index + 2; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex]!;
    // コードブロックの開始フェンスは表より先に確定させる。info string に `|` を含むフェンス行
    // （```` ```sh|bash ````）はパイプを持つためボディ行として成立してしまい、表に飲み込まれると
    // 中身が通常の記法として解釈される。
    if (CODE_FENCE_OPEN.test(rawLineTextOf(text, line))) break;
    const cells = splitTableRowCells(text, line);
    if (cells === null) break;
    body.push({ cells: normalizeTableRowCells(cells, columnCount, line.contentEnd), lineIndex });
  }
  return {
    aligns,
    body,
    columnCount,
    delimiterLineIndex: index + 1,
    header: { cells: headerCells, lineIndex: index },
    headerLineIndex: index,
  };
}

/**
 * エスケープされていないパイプ（セル区切りになるパイプ）の index を左から返す。
 * 直前に連続する `\` の個数が奇数のパイプはエスケープ済みとみなす（`\\|` は `\` のエスケープ +
 * 区切りのパイプ）。連続する `\` は 1 回の走査で数え、`\` が並ぶ本文でも行長に比例した時間で終わる
 * （本関数は本文が変わるたびに全行で呼ばれるため、パイプごとに後方走査すると最悪 O(n^2) になる）。
 */
function unescapedPipeIndexes(content: string): readonly number[] {
  const pipes: number[] = [];
  let backslashes = 0;
  for (let i = 0; i < content.length; i += 1) {
    const character = content[i];
    if (character === "\\") {
      backslashes += 1;
      continue;
    }
    if (character === "|" && backslashes % 2 === 0) pipes.push(i);
    backslashes = 0;
  }
  return pipes;
}

/** セルの前後から取り除く空白（GFM と同じく半角スペースとタブ）。 */
function isTableCellSpace(character: string | undefined): boolean {
  return character === " " || character === "\t";
}

/**
 * 1 行をパイプでセルへ分割し、各セルの raw 範囲（前後の空白を除いた半開区間）を返す。
 * 表の行になり得ない行（plain 以外、またはエスケープされていないパイプが無い行）は null。
 *
 * - 直前に連続する `\` の個数が奇数のパイプだけをエスケープ済みとみなし、セル区切りにしない
 *   （`\\|` は `\` のエスケープ + 区切りのパイプ）。
 * - 行頭・行末のパイプ（前後の空白を除いた最初 / 最後の文字）は、あれば取り除く（空セルを作らない）。
 */
function splitTableRowCells(
  text: string,
  line: MarkdownSourceLine
): readonly MarkdownTableCell[] | null {
  if (line.kind !== "plain") return null;
  const content = line.content;
  const base = line.contentStart;
  const pipes = unescapedPipeIndexes(content);
  if (pipes.length === 0) return null;
  let from = 0;
  while (from < content.length && isTableCellSpace(content[from])) from += 1;
  let to = content.length;
  while (to > from && isTableCellSpace(content[to - 1])) to -= 1;
  let pipeStart = 0;
  let pipeEnd = pipes.length;
  if (pipes[pipeStart] === from) {
    from += 1;
    pipeStart += 1;
  }
  if (pipeEnd > pipeStart && pipes[pipeEnd - 1] === to - 1) {
    to -= 1;
    pipeEnd -= 1;
  }
  const cells: MarkdownTableCell[] = [];
  let cellStart = from;
  for (let p = pipeStart; p < pipeEnd; p += 1) {
    const pipe = pipes[p]!;
    cells.push(trimmedTableCell(content, cellStart, pipe, base));
    cellStart = pipe + 1;
  }
  cells.push(trimmedTableCell(content, cellStart, Math.max(cellStart, to), base));
  return cells;
}

/** `[start, end)` から前後の空白を除いた raw 範囲（本文全体基準）を返す。 */
function trimmedTableCell(
  content: string,
  start: number,
  end: number,
  base: number
): MarkdownTableCell {
  let s = start;
  let e = end;
  while (s < e && isTableCellSpace(content[s])) s += 1;
  while (e > s && isTableCellSpace(content[e - 1])) e -= 1;
  return { end: base + e, start: base + s };
}

/**
 * ボディ行のセル数を列数へそろえる。多い分は切り捨て、足りない分は行末のゼロ幅範囲を持つ
 * 合成セル（`synthesized: true`）で補う。
 */
function normalizeTableRowCells(
  cells: readonly MarkdownTableCell[],
  columnCount: number,
  lineEnd: number
): readonly MarkdownTableCell[] {
  if (cells.length >= columnCount) return cells.slice(0, columnCount);
  const padded = cells.slice();
  while (padded.length < columnCount) {
    padded.push({ end: lineEnd, start: lineEnd, synthesized: true });
  }
  return padded;
}

/**
 * `lineIndex` の行が属する表と、その行の役割（ヘッダ / 区切り / ボディ）を返す。表の外なら undefined。
 * 非編集表示のキャレット写像（web の markdown-wysiwyg.ts）が、行がセルへ分かれているかを判定するために使う。
 */
export function markdownTableAtLine(
  map: MarkdownSourceMap,
  lineIndex: number
): { readonly table: MarkdownTable; readonly row: MarkdownTableRow | undefined } | undefined {
  for (const block of map.blocks) {
    if (block.kind !== "table") continue;
    const { table } = block;
    if (lineIndex < table.headerLineIndex) continue;
    const lastLineIndex = table.delimiterLineIndex + table.body.length;
    if (lineIndex > lastLineIndex) continue;
    if (lineIndex === table.headerLineIndex) return { row: table.header, table };
    if (lineIndex === table.delimiterLineIndex) return { row: undefined, table };
    return { row: table.body[lineIndex - table.delimiterLineIndex - 1], table };
  }
  return undefined;
}

/**
 * セルの「画面に見える」表示テキスト（`\|` は `|` へ畳み、リンクは表示テキストへ縮約し、
 * 装飾マーカーは除く）。列幅の自然幅とプレビュー縮約が共有する。
 */
export function markdownTableCellDisplayText(text: string, cell: MarkdownTableCell): string {
  return markdownRangeDisplaySegments(text, cell.start, cell.end, { collapsePipeEscape: true })
    .map((segment) => segment.text)
    .join("");
}

// East Asian Width が W（Wide）/ F（Fullwidth）に該当する主なコードポイント範囲。
// 文字幅モデル（全角 1.0em / 半角 0.5em）の判定に使う。実フォントの字幅とは一致しないが、
// 画面と出力で同じ値になることが目的であり、セル内の折り返しで差は吸収される。
const EAST_ASIAN_WIDE_RANGES: readonly (readonly [number, number])[] = [
  [0x1100, 0x115f],
  [0x2329, 0x232a],
  [0x2e80, 0x303e],
  [0x3041, 0x33ff],
  [0x3400, 0x4dbf],
  [0x4e00, 0x9fff],
  [0xa000, 0xa4cf],
  [0xa960, 0xa97f],
  [0xac00, 0xd7a3],
  [0xf900, 0xfaff],
  [0xfe10, 0xfe19],
  [0xfe30, 0xfe6f],
  [0xff00, 0xff60],
  [0xffe0, 0xffe6],
  [0x1f300, 0x1f64f],
  [0x1f680, 0x1f6ff],
  [0x1f900, 0x1f9ff],
  [0x20000, 0x2fffd],
  [0x30000, 0x3fffd],
];

function isEastAsianWide(codePoint: number): boolean {
  for (const [low, high] of EAST_ASIAN_WIDE_RANGES) {
    if (codePoint < low) return false;
    if (codePoint <= high) return true;
  }
  return false;
}

/**
 * 文字幅モデルによる推定幅（em）。East Asian Wide / Fullwidth のコードポイントを 1.0、
 * それ以外を 0.5 として合計する。走査はコードポイント単位で行い、サロゲートペアを 2 文字と数えない。
 */
export function estimateEmWidth(text: string): number {
  let width = 0;
  for (const character of text) {
    width += isEastAsianWide(character.codePointAt(0) ?? 0) ? 1 : 0.5;
  }
  return width;
}

/** 列幅（em）を 1/1000 em へ切り捨てる。浮動小数の誤差が CSS 値へ漏れず、合計が使用可能幅を超えない。 */
function floorEm(value: number): number {
  return Math.floor(value * 1000) / 1000;
}

/**
 * 表の列幅（em 単位）を決める。
 * - 各列の目標幅は「自然幅 + セル padding（`cellPaddingEm`。1 列あたりの左右合計）」。
 * - 全列の目標幅の合計が使用可能幅に収まるならそのまま使う。
 * - 収まらない場合は、最小列幅（`minColumnEm`）を保ったまま比例縮小する（water-filling）。
 *   最小列幅で確定する列は「目標幅の小さい順に k 列」になり、確定を 1 列増やすと残りの列へ配れる
 *   比率（残り幅 ÷ 残り目標幅の合計）は必ず下がる。したがって「k 列を確定したとき、残りのうち
 *   最も目標幅の小さい列が最小列幅以上になる」最小の k が答えで、目標幅で整列して後方累積和を
 *   持てば各 k の判定は定数時間になる（列数に対して O(C log C)。1 列ずつ確定を繰り返す素朴な
 *   反復は最悪 O(C^2) になるため採らない）。
 * - すべての列を確定してもなお合計が使用可能幅を超える場合（最小列幅の合計が使用可能幅を超える）は、
 *   全列を最小列幅とし、超過は呼び出し側の打ち切りに委ねる。
 * 同じ入力に対して常に同じ結果を返す。
 */
export function resolveMarkdownTableColumnWidths(params: {
  readonly naturalEmWidths: readonly number[];
  readonly availableEm: number;
  readonly cellPaddingEm: number;
  readonly minColumnEm: number;
}): readonly number[] {
  const { availableEm, cellPaddingEm, minColumnEm, naturalEmWidths } = params;
  const targets = naturalEmWidths.map((width) => Math.max(0, width) + cellPaddingEm);
  return fitColumnWidthsToAvailableEm(targets, availableEm, minColumnEm);
}

/**
 * 目標幅の配列を使用可能幅へ収める（water-filling）。目標幅の作り方だけが違う 2 つの入口
 * （自動列幅は「自然幅 + padding」、保存済み列幅はその値そのもの）が同じ縮小規則を共有するため、
 * 手順をここへ 1 つだけ置く。手順と計算量の根拠は {@link resolveMarkdownTableColumnWidths} を参照。
 */
function fitColumnWidthsToAvailableEm(
  targets: readonly number[],
  availableEm: number,
  minColumnEm: number
): readonly number[] {
  const columnCount = targets.length;
  if (columnCount === 0) return [];
  // 目標幅の合計を求め、使用可能幅に収まるかを判定する。
  const targetTotal = targets.reduce((sum, width) => sum + width, 0);
  if (targetTotal <= availableEm) return targets.map(floorEm);
  // 目標幅の昇順。同値の列は元の並び順を保ち、入力が同じなら結果も同じになるようにする。
  const order = targets.map((_target, index) => index);
  order.sort((a, b) => targets[a]! - targets[b]! || a - b);
  // suffixTotals[k] = 「k 番目以降（目標幅の大きい側）の目標幅の合計」。
  const suffixTotals = new Array<number>(columnCount + 1).fill(0);
  for (let index = columnCount - 1; index >= 0; index -= 1) {
    suffixTotals[index] = suffixTotals[index + 1]! + targets[order[index]!]!;
  }
  let fixedCount = columnCount;
  for (let candidate = 0; candidate < columnCount; candidate += 1) {
    const pool = availableEm - candidate * minColumnEm;
    const openTotal = suffixTotals[candidate]!;
    // 残りのうち目標幅が最小の列が最小列幅以上なら、他の残り列も比例配分で最小列幅以上になる。
    const smallestShare = openTotal > 0 ? (pool * targets[order[candidate]!]!) / openTotal : 0;
    if (smallestShare >= minColumnEm) {
      fixedCount = candidate;
      break;
    }
  }
  const pool = availableEm - fixedCount * minColumnEm;
  const openTotal = suffixTotals[fixedCount]!;
  const widths = new Array<number>(columnCount).fill(minColumnEm);
  for (let index = fixedCount; index < columnCount; index += 1) {
    const column = order[index]!;
    widths[column] = openTotal > 0 ? (pool * targets[column]!) / openTotal : 0;
  }
  return widths.map(floorEm);
}

/**
 * 表の使用可能幅（em）。「（本文領域の幅 − 表の外枠線 × 2）÷ 基準フォント px」で、DOM の実測に依らない。
 * `pxScale` は画面の CSS px を出力側の px へ換算する倍率（PDF・サムネイルの出力スケール。画面は 1）。
 * 外枠線は `box-sizing: border-box` の下でも内側の列幅を左右合計 2px 狭めるため、差し引かないと
 * DOM と Canvas の切り取り位置がずれる。DOM と Canvas が同じ関数を通ることで列幅は必ず一致する。
 */
export function markdownTableAvailableEm(
  areaWidthPx: number,
  baseFontPx: number,
  pxScale = 1
): number {
  if (!(baseFontPx > 0)) return 0;
  return Math.max(0, (areaWidthPx - 2 * TABLE_BORDER_PX * pxScale) / baseFontPx);
}

/** ヘッダ指紋の区切り（表示テキストに現れない制御文字を使い、セルの境目を確実に分ける）。 */
const TABLE_HEADER_KEY_SEPARATOR = "\u001f";

/**
 * FNV-1a（32bit）。同じ文字列に対して環境に依らず同じ値を返すことだけが要件で、
 * 暗号用途には使わない（保存済み列幅の対応づけに使う指紋の材料）。
 */
function fnv1a32(text: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    // FNV prime（16777619）の乗算を 32bit で畳む。通常の `*` では精度が足りない。
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * 表のヘッダ行の指紋。ヘッダ各セルの**表示テキスト**を U+001F で連結した全文の
 * FNV-1a 32bit ハッシュを、8 桁の小文字 16 進数で表す。
 *
 * ブロック順の index と列数だけで保存済み列幅を対応づけると、表の削除・並べ替えで
 * 「同じ列数の別の表」へ古い幅が当たる。先頭 N 文字での切り詰めにすると、長いヘッダの
 * 前半が同じ表を区別できないため、全文をハッシュする。
 */
export function markdownTableHeaderKey(text: string, table: MarkdownTable): string {
  const cells = table.header.cells
    .slice(0, table.columnCount)
    .map((cell) => markdownTableCellDisplayText(text, cell));
  return fnv1a32(cells.join(TABLE_HEADER_KEY_SEPARATOR)).toString(16).padStart(8, "0");
}

/**
 * 保存済み列幅を、解析した表と使用可能幅へ適用する。
 * - 未保存、列数不一致、または `headerKey` 不一致なら自動列幅
 *   （{@link resolveMarkdownTableColumnWidths}）を使う。
 * - すべて一致する場合は、その値を「目標幅」として自動列幅と**同じ water-filling 手順**へ通す
 *   （最小列幅を保ったまま、収まらない場合だけ比例縮小する）。単純な一律比例縮小では、
 *   偏った保存幅（例 `[2em, 512em]`）で細い列が最小列幅を下回るか、clamp で合計が
 *   使用可能幅を超える。
 */
export function resolveMarkdownTableColumnWidthsWithOverride(params: {
  readonly naturalEmWidths: readonly number[];
  readonly availableEm: number;
  readonly cellPaddingEm: number;
  readonly minColumnEm: number;
  readonly override?: TableColumnWidths | null | undefined;
  /** 解析した表のヘッダ指紋。`override.headerKey` と一致しなければ `override` を使わない。 */
  readonly headerKey: string;
}): readonly number[] {
  const { availableEm, cellPaddingEm, headerKey, minColumnEm, naturalEmWidths, override } = params;
  if (
    override != null &&
    override.columnCount === naturalEmWidths.length &&
    override.widthsEm.length === override.columnCount &&
    override.headerKey === headerKey
  ) {
    return fitColumnWidthsToAvailableEm(override.widthsEm, availableEm, minColumnEm);
  }
  return resolveMarkdownTableColumnWidths({
    availableEm,
    cellPaddingEm,
    minColumnEm,
    naturalEmWidths,
  });
}

/**
 * 表の各列の幅（em）。各列の自然幅（その列のセルの表示テキストの推定幅の最大値）を求め、
 * 共有の定数（`TABLE_CELL_PADDING_EM` / `TABLE_MIN_COLUMN_EM`）で `resolveMarkdownTableColumnWidths` を呼ぶ。
 * `override` を渡した場合は、列数とヘッダ指紋が一致するときだけ保存済み列幅を目標幅として使う。
 * DOM（`colgroup`）と Canvas 出力がこの結果をそのまま使う。
 */
export function markdownTableColumnWidthsEm(
  text: string,
  table: MarkdownTable,
  availableEm: number,
  override?: TableColumnWidths | null | undefined
): readonly number[] {
  const naturalEmWidths = new Array<number>(table.columnCount).fill(0);
  for (const row of [table.header, ...table.body]) {
    for (const [column, cell] of row.cells.entries()) {
      if (column >= table.columnCount) break;
      const width = estimateEmWidth(markdownTableCellDisplayText(text, cell));
      if (width > naturalEmWidths[column]!) naturalEmWidths[column] = width;
    }
  }
  if (override == null) {
    return resolveMarkdownTableColumnWidths({
      availableEm,
      cellPaddingEm: TABLE_CELL_PADDING_EM,
      minColumnEm: TABLE_MIN_COLUMN_EM,
      naturalEmWidths,
    });
  }
  return resolveMarkdownTableColumnWidthsWithOverride({
    availableEm,
    cellPaddingEm: TABLE_CELL_PADDING_EM,
    headerKey: markdownTableHeaderKey(text, table),
    minColumnEm: TABLE_MIN_COLUMN_EM,
    naturalEmWidths,
    override,
  });
}

//------------------------------------------------------------------------------
// インライン装飾（太字・斜体・取消線・インラインコード）
//------------------------------------------------------------------------------

/** 記法から導かれるインライン装飾（部分書式 run とは独立に持ち、描画時に合成する）。 */
export interface MarkdownInlineDecoration {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly strikethrough: boolean;
  readonly code: boolean;
}

/** 装飾なしを表す共有定数（毎回オブジェクトを作らないため）。 */
export const NO_MARKDOWN_INLINE_DECORATION: MarkdownInlineDecoration = {
  bold: false,
  code: false,
  italic: false,
  strikethrough: false,
};

/** 2 つの装飾が同値か（セグメント分割の境界判定に使う）。 */
export function isSameMarkdownInlineDecoration(
  a: MarkdownInlineDecoration,
  b: MarkdownInlineDecoration
): boolean {
  return (
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.strikethrough === b.strikethrough &&
    a.code === b.code
  );
}

/**
 * 装飾を安定した文字列署名へ変換する。装飾なしは空文字列。
 * 編集 DOM の正準性判定（editorNeedsRebuild）が、装飾の変化も文字列比較だけで検知できるようにする。
 */
export function markdownInlineDecorationSignature(d: MarkdownInlineDecoration): string {
  if (!d.bold && !d.italic && !d.strikethrough && !d.code) return "";
  return `${d.bold ? "b" : ""}${d.italic ? "i" : ""}${d.strikethrough ? "s" : ""}${d.code ? "c" : ""}`;
}

//------------------------------------------------------------------------------
// 表示時（非編集・PDF）の縮約区間
//------------------------------------------------------------------------------

/**
 * 表示テキストの 1 セグメント。`[label](URL)` は label へ縮約済みで、`rawOffsets` が
 * 表示 1 文字ごとの raw offset（本文全体基準）を保持する。
 * 括弧・URL・インライン装飾のマーカーのように「縮約表示では描画対象を持たない」raw 文字は
 * 含まれない。
 */
export interface MarkdownDisplaySegment {
  readonly text: string;
  /** リンクセグメントのときの遷移先 URL（検証済み）。 */
  readonly url?: string | undefined;
  /** `text` の各文字に対応する raw offset。長さは `text.length` と一致する。 */
  readonly rawOffsets: readonly number[];
  /** 記法から導かれるインライン装飾（この区間のすべての文字で同値）。 */
  readonly decoration: MarkdownInlineDecoration;
  /**
   * 行内で 0 から振るコード範囲の識別子。コード範囲の外は undefined。
   * 隣接する 2 つのコードスパンは decoration が同値になるため、装飾だけで分割すると
   * 1 つの範囲へまとまってしまう。padding と背景を範囲ごとに 1 回ずつ当てるために持つ。
   */
  readonly codeRangeIndex?: number | undefined;
}

/**
 * 1 行の可視本文を、非編集表示（StyledLinkifiedText）と同じセグメント列へ分割し、
 * 表示 1 文字ごとの raw offset と、記法から導かれるインライン装飾を付けて返す。
 *
 * - text セグメント・素 URL は raw と表示が 1:1（装飾マーカーを除く）。
 * - 表示テキスト付きリンクは `[` と `](url)` を落とし、ラベルのエスケープ対
 *   （`\[` `\]` `\\` = raw 2 文字 → 表示 1 文字）は表示 1 文字あたり 1 つの raw offset
 *   （エスケープ対象の文字側）へ対応させる。
 * - インライン装飾のマーカー（`**` / `*` / `~~` / `` ` `` / エスケープの `\`）は表示を持たないため
 *   `text` にも `rawOffsets` にも現れない。装飾が変わる位置でセグメントを分ける。
 */
export function markdownLineDisplaySegments(
  line: MarkdownSourceLine
): readonly MarkdownDisplaySegment[] {
  return displaySegmentsOf(line.content, line.contentStart, false);
}

/** `markdownRangeDisplaySegments` の追加指定。 */
export interface MarkdownRangeDisplayOptions {
  /**
   * `\|` を `|` へ畳むか（表のセル専用）。既定は false。
   * true にすると、`\` は表示されず、表示 1 文字あたりの raw offset はエスケープ対象（`|`）の側を
   * 指す（リンクラベルの `\[` と同じ規則）。表以外の行の表示は 1 文字も変わらない。
   */
  readonly collapsePipeEscape?: boolean | undefined;
}

/**
 * 指定した raw 範囲 `[start, end)` を、非編集表示と同じセグメント列へ分割する。
 * 行全体に対する `markdownLineDisplaySegments` と同じ実装（`displaySegmentsOf`）を通るため、
 * 表のセルの中でもリンク・インライン装飾・部分書式が行と同じ規則で働く。
 * `rawOffsets` は本文全体基準（`start` を基点に振る）。
 */
export function markdownRangeDisplaySegments(
  text: string,
  start: number,
  end: number,
  options?: MarkdownRangeDisplayOptions | undefined
): readonly MarkdownDisplaySegment[] {
  const lo = Math.max(0, Math.min(start, end));
  const hi = Math.min(text.length, Math.max(start, end));
  return displaySegmentsOf(text.slice(lo, hi), lo, options?.collapsePipeEscape === true);
}

/**
 * 可視本文 `content`（raw 開始 offset = `base`）を表示セグメント列へ分割する共通実装。
 * 行版・範囲版のどちらもここを通る（表のセルと行の表示規則を 1 つに保つため）。
 */
function displaySegmentsOf(
  content: string,
  base: number,
  collapsePipeEscape: boolean
): readonly MarkdownDisplaySegment[] {
  const analysis = analyzeInlineContent(content);
  const hidden = collapsePipeEscape
    ? withPipeEscapesHidden(content, analysis.hidden)
    : analysis.hidden;
  const segments: MarkdownDisplaySegment[] = [];
  for (const piece of analysis.pieces) {
    let text = "";
    let rawOffsets: number[] = [];
    let decoration: MarkdownInlineDecoration | null = null;
    let codeRangeIndex: number | undefined;
    const flush = (): void => {
      if (decoration !== null && text.length > 0) {
        segments.push({
          decoration,
          rawOffsets,
          text,
          ...(piece.url !== undefined ? { url: piece.url } : {}),
          ...(codeRangeIndex !== undefined ? { codeRangeIndex } : {}),
        });
      }
      text = "";
      rawOffsets = [];
      decoration = null;
      codeRangeIndex = undefined;
    };
    for (let i = piece.start; i < piece.end; i += 1) {
      if (hidden[i] === true) continue;
      const charDecoration = analysis.decorations[i] ?? NO_MARKDOWN_INLINE_DECORATION;
      const charCodeRangeIndex = analysis.codeRangeIndexes[i];
      if (
        decoration !== null &&
        (!isSameMarkdownInlineDecoration(charDecoration, decoration) ||
          charCodeRangeIndex !== codeRangeIndex)
      ) {
        flush();
      }
      if (decoration === null) {
        decoration = charDecoration;
        codeRangeIndex = charCodeRangeIndex;
      }
      text += content[i];
      rawOffsets.push(base + i);
    }
    flush();
  }
  return segments;
}

/**
 * 表のセル専用: `\|`（直前に連続する `\` の個数が奇数のパイプ）の `\` を非表示位置へ加える。
 * 表のセル分割と同じ奇偶判定を使い、セル区切りにならなかったパイプの直前の `\` だけを畳む。
 * インライン装飾のエスケープ（`\*` 等）とは独立で、コード範囲の内側でも畳む（GFM と同じく、
 * 表のパイプのエスケープは行内記法より先に解決されるため）。
 */
function withPipeEscapesHidden(content: string, hidden: readonly boolean[]): readonly boolean[] {
  if (!content.includes("|")) return hidden;
  // エスケープされていないパイプの index を得て、その補集合（= エスケープ済みのパイプ）の
  // 直前の `\` を非表示にする。判定はセル分割とまったく同じ走査を共有する。
  const unescaped = new Set(unescapedPipeIndexes(content));
  const result = hidden.slice();
  for (let i = 0; i < content.length; i += 1) {
    if (content[i] !== "|" || unescaped.has(i)) continue;
    result[i - 1] = true;
  }
  return result;
}

/**
 * 行の可視本文における raw offset を、非編集表示のテキスト上のオフセットへ換算する。
 * 表示セグメントの `rawOffsets`（表示 1 文字ごとの raw offset）を数え、
 * 「rawOffset より小さい raw offset を持つ表示文字の個数」を返す。
 * マーカー・括弧・URL のように表示を持たない raw 位置は、直後の表示文字の位置へ寄る。
 *
 * `rawOffset` は本文全体の絶対 offset（`line.contentStart` と同じ座標系）である。
 */
export function displayOffsetInLine(line: MarkdownSourceLine, rawOffset: number): number {
  return countDisplayCharsBefore(markdownLineDisplaySegments(line), rawOffset);
}

/**
 * raw 範囲 `[start, end)`（表のセルなど）における raw offset を、その範囲の表示テキスト上の
 * オフセットへ換算する。数え方は `displayOffsetInLine` と同じ（表示を持たない raw 位置は直後の
 * 表示文字の位置へ寄り、`end` 以降は表示文字数になる）。
 */
export function displayOffsetInRange(
  text: string,
  start: number,
  end: number,
  rawOffset: number,
  options?: MarkdownRangeDisplayOptions | undefined
): number {
  return countDisplayCharsBefore(
    markdownRangeDisplaySegments(text, start, end, options),
    rawOffset
  );
}

/** 表示セグメント列のうち、`rawOffset` より小さい raw offset を持つ表示文字の個数。 */
function countDisplayCharsBefore(
  segments: readonly MarkdownDisplaySegment[],
  rawOffset: number
): number {
  let count = 0;
  for (const segment of segments) {
    for (const offset of segment.rawOffsets) {
      if (offset < rawOffset) count += 1;
    }
  }
  return count;
}

/** マーカーを含む装飾範囲（編集 DOM の span 分割に使う平坦な区間）。 */
export interface MarkdownInlineDecorationRange {
  /** 本文全体基準の raw 開始 offset。 */
  readonly start: number;
  /** 本文全体基準の raw 終端 offset（半開区間）。 */
  readonly end: number;
  readonly decoration: MarkdownInlineDecoration;
}

/**
 * 行の可視本文を、マーカーを含む装飾範囲で**平坦に分割**した結果（編集 DOM の span 分割に使う）。
 * 入れ子（`**太字の中の *斜体* **`）は入れ子のまま返さず、実効的な装飾を持つ
 * 重なりの無い区間列へ畳んで返す。区間は start 昇順で隣接し、行の可視本文を過不足なく覆う。
 * 装飾の無い区間も `decoration` がすべて false の区間として含む。
 *
 * マーカー文字は、その種類のトグルの前後の装飾の和を持つ（開始マーカーはトグル後、
 * 終了マーカーはトグル前）。これにより `**重要**` は 6 文字が 1 つの区間になる。
 */
export function markdownInlineDecorationRanges(
  line: MarkdownSourceLine
): readonly MarkdownInlineDecorationRange[] {
  if (line.content.length === 0) return [];
  if (!INLINE_MARKER_PATTERN.test(line.content)) {
    // 記法文字を 1 つも含まない行は装飾になり得ない。編集中は毎入力ごとに呼ばれるため、
    // リンク分割まで含む解析を省いて可視本文 1 区間だけを返す。
    return [
      { decoration: NO_MARKDOWN_INLINE_DECORATION, end: line.contentEnd, start: line.contentStart },
    ];
  }
  const analysis = analyzeInlineContent(line.content);
  const ranges: MarkdownInlineDecorationRange[] = [];
  for (let i = 0; i < line.content.length; i += 1) {
    const decoration = analysis.decorations[i] ?? NO_MARKDOWN_INLINE_DECORATION;
    const last = ranges[ranges.length - 1];
    if (last !== undefined && isSameMarkdownInlineDecoration(last.decoration, decoration)) {
      ranges[ranges.length - 1] = {
        decoration: last.decoration,
        end: last.end + 1,
        start: last.start,
      };
      continue;
    }
    ranges.push({
      decoration,
      end: line.contentStart + i + 1,
      start: line.contentStart + i,
    });
  }
  return ranges;
}

/**
 * 本文にインライン装飾が 1 つでも成立するか。
 * Canvas レイアウトが「従来どおり行単位でまとめて計測する経路」と「1 文字ずつフォントを
 * 切り替える装飾対応経路」を切り替えるための述語。装飾が 1 つも無い本文では従来経路を
 * そのまま通し、既存の出力を変えない。
 */
export function hasMarkdownInlineDecorations(text: string): boolean {
  for (const line of buildMarkdownSourceMap(text).lines) {
    if (!INLINE_MARKER_PATTERN.test(line.content)) continue;
    for (const decoration of analyzeInlineContent(line.content).decorations) {
      if (!isSameMarkdownInlineDecoration(decoration, NO_MARKDOWN_INLINE_DECORATION)) return true;
    }
  }
  return false;
}

//------------------------------------------------------------------------------
// インライン装飾のトークナイズ（内部実装）
//------------------------------------------------------------------------------

/** エスケープ（`\`）で literal にできる記法文字。 */
const INLINE_ESCAPABLE_CHARS = new Set(["*", "~", "`", "\\"]);

/** 表示テキスト付きリンクのラベル内でエスケープできる文字（`unescapeLinkLabel` と同じ集合）。 */
const LINK_LABEL_ESCAPABLE_CHARS = new Set(["\\", "[", "]"]);

/**
 * 行の可視本文が装飾記法を含み得るか。1 文字も含まない行は解析そのものを省き、
 * 装飾を使っていない既存本文の処理コストを増やさない。
 */
const INLINE_MARKER_PATTERN = /[*~`\\]/u;

/** 装飾マーカーの 1 トークン（種類と raw 区間）。 */
interface InlineDecorationToken {
  readonly kind: "bold" | "italic" | "strikethrough";
  readonly start: number;
  readonly length: number;
}

/** 表示のまとまり（素テキスト・コード範囲・リンク）。`url` があればリンクとして描画する。 */
interface InlineContentPiece {
  readonly start: number;
  readonly end: number;
  readonly url?: string | undefined;
}

/** 1 行の可視本文を、リンク・コード範囲・装飾の観点で解析した結果。 */
interface InlineContentAnalysis {
  /** 表示のまとまり。content 全体を過不足なく覆い、start 昇順で隣接する。 */
  readonly pieces: readonly InlineContentPiece[];
  /** content の各 index の装飾（マーカー文字はトグル前後の和）。 */
  readonly decorations: readonly MarkdownInlineDecoration[];
  /** 表示に現れない index（装飾マーカー・エスケープの `\`・リンク記法の構造文字・バッククォート）。 */
  readonly hidden: readonly boolean[];
  /** index が属するコード範囲の識別子（行内 0 始まり。範囲外は undefined）。 */
  readonly codeRangeIndexes: readonly (number | undefined)[];
}

/**
 * 行の可視本文からインラインコード範囲（バッククォート込み）を左から確定する。
 * コード範囲は最優先で決まり、内部は一切解釈しない（エスケープも効かない）。
 * 対応する閉じバッククォートが無い場合は literal の文字として扱う。
 */
function findInlineCodeRanges(content: string): readonly RawOffsetRange[] {
  const ranges: RawOffsetRange[] = [];
  let i = 0;
  while (i < content.length) {
    const ch = content[i]!;
    if (ch === "\\" && i + 1 < content.length && INLINE_ESCAPABLE_CHARS.has(content[i + 1]!)) {
      // エスケープされたバッククォートはコード範囲を開かない。
      i += 2;
      continue;
    }
    if (ch === "`") {
      const close = content.indexOf("`", i + 1);
      if (close < 0) {
        i += 1;
        continue;
      }
      ranges.push({ end: close + 1, start: i });
      i = close + 1;
      continue;
    }
    i += 1;
  }
  return ranges;
}

/** すべて false の装飾では共有定数を返し、無駄なオブジェクト生成と比較コストを避ける。 */
function createInlineDecoration(
  bold: boolean,
  italic: boolean,
  strikethrough: boolean,
  code: boolean
): MarkdownInlineDecoration {
  if (!bold && !italic && !strikethrough && !code) return NO_MARKDOWN_INLINE_DECORATION;
  return { bold, code, italic, strikethrough };
}

/**
 * 行の可視本文を解析し、表示のまとまり・装飾・非表示位置・コード範囲の識別子を返す。
 *
 * 順序（設計 §9.4）:
 * 1. インラインコード範囲を先に確定する。
 * 2. コード範囲を除いた各断片へ既存の `splitTextIntoLinkSegments` を適用する
 *    （既存関数のシグネチャも挙動も変更しない）。
 * 3. 素 URL と表示テキスト付きリンクの非ラベル部分をマスクし、content 全体へ装飾トークナイザを走らせる。
 */
function analyzeInlineContent(content: string): InlineContentAnalysis {
  const length = content.length;
  const decorations = new Array<MarkdownInlineDecoration>(length).fill(
    NO_MARKDOWN_INLINE_DECORATION
  );
  const hidden = new Array<boolean>(length).fill(false);
  const codeRangeIndexes = new Array<number | undefined>(length).fill(undefined);
  const mask = new Array<boolean>(length).fill(false);
  const hasMarker = INLINE_MARKER_PATTERN.test(content);
  const codeRanges = hasMarker ? findInlineCodeRanges(content) : [];
  for (const [index, range] of codeRanges.entries()) {
    for (let i = range.start; i < range.end; i += 1) {
      mask[i] = true;
      codeRangeIndexes[i] = index;
    }
    // 開始・終了のバッククォートは表示しない（`[label](url)` の括弧と同じ扱い）。
    hidden[range.start] = true;
    hidden[range.end - 1] = true;
  }
  const pieces: InlineContentPiece[] = [];
  let cursor = 0;
  for (const range of resolveCodeSplitRanges(content, codeRanges)) {
    if (range.start > cursor) {
      collectLinkPieces(content, cursor, range.start, pieces, mask, hidden);
    }
    pieces.push({ end: range.end, start: range.start });
    cursor = range.end;
  }
  if (cursor < length) {
    collectLinkPieces(content, cursor, length, pieces, mask, hidden);
  }
  if (!hasMarker) {
    return { codeRangeIndexes, decorations, hidden, pieces };
  }
  const { escapes, tokens } = collectInlineDecorationTokens(content, mask);
  const counts = { bold: 0, italic: 0, strikethrough: 0 };
  for (const token of tokens) counts[token.kind] += 1;
  // 種類ごとに、その行でのトグル回数が奇数なら成立しない（マーカーは literal の文字に戻る）。
  const established = {
    bold: counts.bold % 2 === 0,
    italic: counts.italic % 2 === 0,
    strikethrough: counts.strikethrough % 2 === 0,
  };
  const tokensByStart = new Map<number, InlineDecorationToken>();
  for (const token of tokens) {
    if (established[token.kind]) tokensByStart.set(token.start, token);
  }
  const escapeStarts = new Set(escapes);
  let bold = false;
  let italic = false;
  let strikethrough = false;
  for (let index = 0; index < length; ) {
    const token = tokensByStart.get(index);
    if (token !== undefined) {
      const before = { bold, italic, strikethrough };
      if (token.kind === "bold") bold = !bold;
      else if (token.kind === "italic") italic = !italic;
      else strikethrough = !strikethrough;
      // マーカー文字はトグル前後の和を持つ（開始マーカーはトグル後、終了マーカーはトグル前）。
      const markerDecoration = createInlineDecoration(
        before.bold || bold,
        before.italic || italic,
        before.strikethrough || strikethrough,
        codeRangeIndexes[index] !== undefined
      );
      for (let i = index; i < index + token.length; i += 1) {
        hidden[i] = true;
        decorations[i] = markerDecoration;
      }
      index += token.length;
      continue;
    }
    if (escapeStarts.has(index)) hidden[index] = true;
    decorations[index] = createInlineDecoration(
      bold,
      italic,
      strikethrough,
      codeRangeIndexes[index] !== undefined
    );
    index += 1;
  }
  return { codeRangeIndexes, decorations, hidden, pieces };
}

/**
 * コード範囲のうち、リンク分割の走査を区切る「分割点」だけを返す。
 *
 * 表示テキスト付きリンクのラベルの内側に完全に収まるコード範囲は分割点にしない。
 * 分割点にすると `[`code`](URL)` のようにラベルがコードのリンクで、`[`・コード範囲・`](URL)` が
 * 別々の断片になり、リンクとして確定できなくなるためである（記法と URL が表示へ残る）。
 * 分割点から外してもコード範囲そのものは有効で、マスク・装飾・コード範囲の識別子は変わらない。
 *
 * リンク記法そのものへ食い込むコード範囲（`` `[a](URL)` `` のようにリンク全体を包む場合や、
 * URL 側にバッククォートがある場合）は、§9.3 の「コードを最優先で確定する」に従って分割点のまま残す。
 */
function resolveCodeSplitRanges(
  content: string,
  codeRanges: readonly RawOffsetRange[]
): readonly RawOffsetRange[] {
  if (codeRanges.length === 0) return codeRanges;
  // コード範囲もリンクセグメントも start 昇順・非重複のため、走査位置を持ち回れば
  // 全体を 1 回ずつ見るだけで済む（リンクとコードを多数並べた行でも線形に収まる）。
  const labelRegions: RawOffsetRange[] = [];
  let codeCursor = 0;
  let offset = 0;
  for (const segment of splitTextIntoLinkSegments(content)) {
    const raw = segment.raw ?? segment.value;
    const end = offset + raw.length;
    if (segment.type === "link" && segment.raw !== undefined && segment.url !== undefined) {
      const labelRawLength = raw.length - segment.url.length - 4;
      const labelStart = offset + 1;
      const labelEnd = labelStart + labelRawLength;
      while (codeCursor < codeRanges.length && codeRanges[codeCursor]!.end <= offset) {
        codeCursor += 1;
      }
      let intact = labelRawLength > 0;
      for (let i = codeCursor; intact && i < codeRanges.length; i += 1) {
        const range = codeRanges[i]!;
        if (range.start >= end) break;
        // リンク記法そのものへ食い込むコード範囲が 1 つでもあれば、コードを優先する。
        if (range.start < labelStart || range.end > labelEnd) intact = false;
      }
      if (intact) labelRegions.push({ end: labelEnd, start: labelStart });
    }
    offset = end;
  }
  if (labelRegions.length === 0) return codeRanges;
  const splitRanges: RawOffsetRange[] = [];
  let regionCursor = 0;
  for (const range of codeRanges) {
    while (regionCursor < labelRegions.length && labelRegions[regionCursor]!.end <= range.start) {
      regionCursor += 1;
    }
    const region = labelRegions[regionCursor];
    const containedInLabel =
      region !== undefined && range.start >= region.start && range.end <= region.end;
    if (!containedInLabel) splitRanges.push(range);
  }
  return splitRanges;
}

/**
 * `[from, to)` の断片へ既存のリンク分割を適用し、表示のまとまりとマスク・非表示位置を積む。
 * 素 URL は全体をマスクし、表示テキスト付きリンクは `[` と `](url)` だけをマスク・非表示にする
 * （ラベル本文はマスクしないため、ラベル内の装飾記法は解釈される）。
 */
function collectLinkPieces(
  content: string,
  from: number,
  to: number,
  pieces: InlineContentPiece[],
  mask: boolean[],
  hidden: boolean[]
): void {
  let offset = from;
  for (const segment of splitTextIntoLinkSegments(content.slice(from, to))) {
    const raw = segment.raw ?? segment.value;
    const end = offset + raw.length;
    if (segment.type !== "link" || segment.url === undefined) {
      pieces.push({ end, start: offset });
      offset = end;
      continue;
    }
    if (segment.raw === undefined) {
      // 素 URL は全体が URL 本体。装飾マーカーになり得ないものとしてマスクする。
      for (let i = offset; i < end; i += 1) mask[i] = true;
      pieces.push({ end, start: offset, url: segment.url });
      offset = end;
      continue;
    }
    const labelRawLength = raw.length - segment.url.length - 4;
    if (labelRawLength < 0) {
      // 想定外の構造。安全側でテキストとして扱う（旧挙動と同じ線形対応）。
      pieces.push({ end, start: offset });
      offset = end;
      continue;
    }
    mask[offset] = true;
    hidden[offset] = true;
    for (let i = offset + 1 + labelRawLength; i < end; i += 1) {
      mask[i] = true;
      hidden[i] = true;
    }
    // ラベル内のリンクエスケープ（`\\` `\[` `\]`）の `\` は表示しない（raw 2 文字 → 表示 1 文字）。
    for (let k = 0; k < labelRawLength; k += 1) {
      if (
        raw[1 + k] === "\\" &&
        k + 1 < labelRawLength &&
        LINK_LABEL_ESCAPABLE_CHARS.has(raw[2 + k]!)
      ) {
        hidden[offset + 1 + k] = true;
        k += 1;
      }
    }
    pieces.push({ end, start: offset, url: segment.url });
    offset = end;
  }
}

/**
 * 装飾マーカーのトークン列と、エスケープの `\` の位置を集める（1 行 1 走査）。
 * 連続する `*` の run は `**` を floor(N/2) 個と、N が奇数なら `*` を 1 個へ分解する。
 * `~` の run は `~~` を floor(N/2) 個へ分解し、余った 1 個は文字として扱う。
 */
function collectInlineDecorationTokens(
  content: string,
  mask: readonly boolean[]
): { readonly tokens: readonly InlineDecorationToken[]; readonly escapes: readonly number[] } {
  const tokens: InlineDecorationToken[] = [];
  const escapes: number[] = [];
  const length = content.length;
  let i = 0;
  while (i < length) {
    if (mask[i] === true) {
      i += 1;
      continue;
    }
    const ch = content[i]!;
    if (
      ch === "\\" &&
      i + 1 < length &&
      mask[i + 1] !== true &&
      INLINE_ESCAPABLE_CHARS.has(content[i + 1]!)
    ) {
      escapes.push(i);
      // 直後の 1 文字は literal。マーカー候補として読み直さない。
      i += 2;
      continue;
    }
    if (ch !== "*" && ch !== "~") {
      i += 1;
      continue;
    }
    let end = i;
    while (end < length && mask[end] !== true && content[end] === ch) end += 1;
    const runLength = end - i;
    const pairs = Math.floor(runLength / 2);
    const pairKind = ch === "*" ? "bold" : "strikethrough";
    for (let k = 0; k < pairs; k += 1) {
      pushInlineDecorationToken(tokens, content, pairKind, i + k * 2, 2);
    }
    if (ch === "*" && runLength % 2 === 1) {
      pushInlineDecorationToken(tokens, content, "italic", i + pairs * 2, 1);
    }
    i = end;
  }
  return { escapes, tokens };
}

/**
 * マーカーとして数えられるトークンだけを積む。
 * 開始になり得る（直後が空白でも行末でもない）か、終了になり得る（直前が空白でも行頭でもない）
 * かのいずれかを満たす必要がある（CommonMark の left/right-flanking の簡略版）。
 * これにより `2 * 3 * 4` の `*` はマーカーにならない。
 */
function pushInlineDecorationToken(
  tokens: InlineDecorationToken[],
  content: string,
  kind: InlineDecorationToken["kind"],
  start: number,
  length: number
): void {
  const next = content[start + length];
  const previous = content[start - 1];
  const canOpen = next !== undefined && !/\s/u.test(next);
  const canClose = previous !== undefined && !/\s/u.test(previous);
  if (!canOpen && !canClose) return;
  tokens.push({ kind, length, start });
}
